'use client';

import { useState, useEffect, useMemo, useRef, useDeferredValue } from 'react';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowUp,
  Check,
  ChevronDown,
  Clock,
  Copy,
  ImagePlus,
  Pencil,
  Trash2,
  Search,
  Settings,
  Sun,
  Moon,
  Monitor,
  ChevronUp,
  Upload,
  Sparkles,
  Atom,
  X,
  Loader2,
  // 2026-07-30 v2 老板需求：去"习题演练"/"进入习题"按钮后，新增"学员详情"按钮用 UserCircle2 图标
  UserCircle2,
  // 【v6.97.147 2026-09-17 P0 老板需求】"管理课程/近期上课课程"卡片点击折叠/展开
  PanelRightClose,
  Library,
} from 'lucide-react';
import { useI18n } from '@/lib/hooks/use-i18n';
import { useConfigVersionPolling } from '@/lib/hooks/use-config-version-polling';
import { LanguageSwitcher } from '@/components/language-switcher';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
// 【2026-06-12 修复】VisuallyHidden 必须从 @radix-ui/react-visually-hidden 导入（不是 'radix-ui'）。
//   radix-ui 包没有 default export 的 VisuallyHidden，导入会是 undefined →
//   "Element type is invalid: expected a string ... but got: object" runtime error →
//   整个 HomePage 客户端 hydration 崩到 root。改用纯 HTML sr-only span 替代（Tailwind 默认 class，0 依赖）。
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { createLogger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupInput, InputGroupButton } from '@/components/ui/input-group';
import { Textarea as UITextarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { SettingsDialog } from '@/components/settings';
import { GenerationToolbar } from '@/components/generation/generation-toolbar';
import { AgentBar } from '@/components/agent/agent-bar';
import { useTheme } from '@/lib/hooks/use-theme';
import { nanoid } from 'nanoid';
import { storePdfBlob } from '@/lib/utils/image-storage';
import type { UserRequirements, SelectedCourseMaterial } from '@/lib/types/generation';
// 【2026-07-29 多文件上传 additive】镜像官方 OpenMAIC 多文件上传
import { dedupeCourseMaterialFiles } from '@/lib/document/course-materials';
import { ImageUploadButton, type UploadedImage } from '@/components/upload/image-upload-button';
import { useSettingsStore } from '@/lib/store/settings';
import { useUserProfileStore, AVATAR_OPTIONS } from '@/lib/store/user-profile';
import {
  StageListItem,
  listStages,
  deleteStageData,
  renameStage,
  getFirstSlideByStages,
  saveStageMetaOnly,
  saveStageMetaOnlyBatch,
} from '@/lib/storage-bridge/stage-storage-v2';
import { ThumbnailSlide } from '@/components/slide-renderer/components/ThumbnailSlide';
import type { Slide } from '@/lib/types/slides';
import { useMediaGenerationStore } from '@/lib/store/media-generation';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDraftCache } from '@/lib/hooks/use-draft-cache';
import { SpeechButton } from '@/components/audio/speech-button';
import { useImportClassroom } from '@/lib/import/use-import-classroom';
import { CourseCatalogCard } from '@/app/components/course-catalog-card';
import { AiPlanTooltip } from '@/components/ai-plan-tooltip';

import { useAuthStore } from '@/lib/store/auth';
import { TrialOfferPopup } from '@/components/trial-offer-popup';
import { TopNav } from '@/components/top-nav';
import NoticeBell from '@/components/notice-bell';

const log = createLogger('Home');

// Wrapper for fixed right-side positioning of CourseCatalogCard
// ★ 2026-06-08 修复：280px 宽 + right: 1rem 在 1280px 视口下跟 hero(240-1040) 重叠 56px
// 改为 width: 240px; right: 0 — 跟 hero(240-1040) 完全无重叠 (侧栏 1040-1280)
// 同时保持 fixed 定位，不受父容器 scroll 影响
// ★ v6.70 (2026-07-12 老板反馈「卡片做宽一点, 一行能显示更多内容」):
//   响应式加宽: lg (1024-1280) 保持 240px (不回归老用户), xl (≥1280) 变 280px
//   - 1280px 视口: hero 800px (max-w-[800px]) 居中 = 240-1040, sidebar 280px 占 1000-1280
//     → 重叠 40px (右边, 输入框核心区 240-1040 不被覆盖, 只有右边缘按钮可能被压)
//   - 1024px 视口: sidebar 240px, hero 居中 112-912, sidebar 784-1024 → 重叠 128px (同原版)
//   - 老板实际工作用 1920px / 2K, 视口足够宽, 无重叠
//   - ≥ lg (1024px) 显示, 中小屏继续 hidden
// ★ v6.71 (2026-07-12 老板反馈「卡片可以拖动, 也可以拉宽拉长」):
//   - 默认位置保持原 fixed right:0 top:50% translateY(-50%)
//   - 卡片头部 (课程头部紫色渐变区域) 可拖动 → 整卡跟随移动 (mousedown + mousemove + mouseup)
//   - 右下角 resize handle (8×8 紫色三角) 可拉宽/拉长 (左下角拉长改变 height, 右下角拉宽改变 width)
//   - 位置 + 尺寸持久化到 localStorage `dongniai_sidebar_geom` (key: pos x/y + size w/h)
//   - 拖动边界: 不能拖出视口 (clamp 到视口内)
//   - 最小尺寸: 240×300 (避免卡片内容被压扁)
//   - 最大尺寸: 视口宽 - 40 / 视口高 - 40
//   - 不动 CourseCatalogCard 组件本身

// ★ v6.97.86 (2026-09-04 P0 修复): 公共 helper —— 自动找 + 自动点"进入课堂"按钮
//   解决 3 处重复 (L624 / L731 / L806) + 3 大原 bug:
//     B: 原来 L624 用 `button[title*="进入课堂"]` selector, 但生产代码 button 没 title 属性 → 100% 找不到
//     C: 原来固定 setTimeout 600/800ms, React re-render 慢时按钮 disabled=true 漏 click
//     D: 三个地方 selector 各不一致 (title vs textContent vs querySelectorAll)
//   新版: 优先用 title/aria-label, fallback 用 textContent; 循环重试 (50ms/次, 3s 超时)
//   等按钮 enabled 后才 click; 返回 boolean 表示是否真的 click 了
function tryAutoClickEnterClassroom(opts?: { timeoutMs?: number; intervalMs?: number }): boolean {
  if (typeof window === 'undefined') return false;
  const timeoutMs = opts?.timeoutMs ?? 3000;
  const intervalMs = opts?.intervalMs ?? 50;
  const deadline = Date.now() + timeoutMs;
  // 复用"找按钮"逻辑
  const findBtn = (): HTMLButtonElement | null => {
    // 优先: title 或 aria-label 包含中英文 (兼容老 L624 路径)
    const titled = document.querySelector(
      'button[title*="进入课堂"], button[title*="Enter Classroom"], button[aria-label*="进入课堂"], button[aria-label*="Enter Classroom"]'
    ) as HTMLButtonElement | null;
    if (titled) return titled;
    // 兜底: textContent 扫一遍 (兼容 L731 / L806 路径)
    for (const btn of Array.from(document.querySelectorAll('button'))) {
      const t = (btn.textContent || '').trim();
      if (t.includes('进入课堂') || t.includes('Enter Classroom')) {
        return btn as HTMLButtonElement;
      }
    }
    return null;
  };
  // 同步先试一次
  const immediate = findBtn();
  if (immediate && !immediate.hasAttribute('disabled')) {
    immediate.click();
    return true;
  }
  // 否则循环等到 enabled (避免 React re-render 时序问题)
  // 用 setInterval 实现, 避免 setTimeout 嵌套
  let clicked = false;
  const timer = window.setInterval(() => {
    if (clicked) return;
    if (Date.now() > deadline) {
      window.clearInterval(timer);
      return;
    }
    const btn = findBtn();
    if (btn && !btn.hasAttribute('disabled')) {
      clicked = true;
      window.clearInterval(timer);
      btn.click();
    }
  }, intervalMs);
  return true; // 已派发轮询, 真正的 click 异步发生
}

function CourseCatalogCardWrapper() {
  // ★ v6.73 (2026-07-12 老板反馈): 最小尺寸 = 280×360 (足够展示一行课程标题+分页器)
  //  不能再小,否则卡片内容挤压不可读
  const MIN_W = 280;
  const MIN_H = 360;
  // sidebar 任意方向至少保留 MIN_VISIBLE_PX 在视口内 (防拖出页面)
  const MIN_VISIBLE_PX = 60;
  // ★ v6.80 (2026-07-12 老板反馈): 大厂级默认位置/大小
  //   - TOP_OFFSET: 顶部 64px (在 app header 下方, 像 Linear/Notion sidebar)
  //   - SIDE_MARGIN: 距屏边 20px (浮窗感, 不贴边)
  const TOP_OFFSET = 64;
  const SIDE_MARGIN = 20;
  // 默认尺寸 (大厂标准: 300×600, 内容超过时 Card 内部滚动)
  const DEFAULT_W_DESKTOP = 300;
  const DEFAULT_W_MOBILE = 280;
  const DEFAULT_H = 600;
  // sidebar 默认位置/尺寸 state (偏移量 + 自定义尺寸, 0 表示用默认值)
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [hydrated, setHydrated] = useState(false);
  const [vw, setVw] = useState(1280); // 默认 SSR 占位, hydration 后 useEffect 更新
  // ★ v6.80: 新增 vh state 用于计算默认 height (自适应小屏幕)
  const [vh, setVh] = useState(720); // 默认 SSR 占位 (避免 SSR 渲染 vh=undefined 报错)
  const [resizing, setResizing] = useState(false); // resize 进行中, 显示 dashed 边
  const [resizeHint, setResizeHint] = useState<string>(''); // 实时显示 "W × H"
  const [hoverEdge, setHoverEdge] = useState<string>(''); // 鼠标悬停边缘 (显示高亮)
  const [isFullscreen, setIsFullscreen] = useState(false); // 全屏模式 (双击放大镜触发)
  // 【v6.97.147 2026-09-17 P0 老板需求】sidebar 折叠/展开状态
  //   - 默认 false (展开) — 老板原话"默认为展开状态"
  //   - true 时整个 sidebar 收起, 右下角只剩悬浮按钮
  //   - 持久化到 localStorage `dongniai_sidebar_collapsed`
  const [collapsed, setCollapsed] = useState(false);

  // 加载 localStorage + 视口宽度
  useEffect(() => {
    // 【v6.97.147】优先读取折叠状态 (默认 false)
    try {
      const c = localStorage.getItem('dongniai_sidebar_collapsed');
      if (c === 'true') setCollapsed(true);
    } catch {}
    let loadedPos = { x: 0, y: 0 };
    let loadedSize = { w: 0, h: 0 };
    try {
      const raw = localStorage.getItem('dongniai_sidebar_geom');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.pos && typeof parsed.pos.x === 'number' && typeof parsed.pos.y === 'number') {
          loadedPos = { x: parsed.pos.x, y: parsed.pos.y };
        }
        if (parsed.size && typeof parsed.size.w === 'number' && typeof parsed.size.h === 'number') {
          loadedSize = { w: parsed.size.w, h: parsed.size.h };
        }
      }
    } catch {}
    const vwNow = window.innerWidth;
    const vhNow = window.innerHeight;
    // ★ v6.77 (2026-07-12 老板反馈): 卡拉长后底部空白 + sanitize 误判
    //   v6.74 sanitize 把 size.h > vh-40 的情况误判为"飞出屏"并 reset
    //   真因: clampPos 公式在 size 超出视口时 minY > maxY, safe 计算异常
    //   修法: 只在 loadedPos 自身极端时 reset (|pos| > 500 表示真飞屏);
    //         size 超出视口自动 clamp 到视口内, 不 reset
    const w = Math.max(MIN_W, loadedSize.w || (vwNow >= 1280 ? 280 : 240));
    const h = Math.max(MIN_H, loadedSize.h || 480);
    // size 也按视口 clamp (保留老板的"拉长"操作, 但不超出视口)
    const clampedSizeW = Math.min(Math.max(MIN_W, w), vwNow - 40);
    const clampedSizeH = Math.min(Math.max(MIN_H, h), vhNow - 40);
    const safe = clampPos(loadedPos, clampedSizeW, clampedSizeH, vwNow, vhNow);
    // 只看 loadedPos 自身是否极端 (例如 v6.72 之前的 -5000)
    // ★ v6.80: 阈值从 500 改 2000 — 新坐标 minY=-604, maxY=596, maxX=260, minX=-1200, 旧 500 会误判 reset
    const posOutOfView = Math.abs(loadedPos.x) > 2000 || Math.abs(loadedPos.y) > 2000;
    if (posOutOfView) {
      console.warn('[sidebar] localStorage 飞出视口,自动 reset:', loadedPos);
      loadedPos = { x: 0, y: 0 };
      loadedSize = { w: 0, h: 0 };
      try { localStorage.removeItem('dongniai_sidebar_geom'); } catch {}
    } else {
      setPos(safe);
      if (loadedSize.w) setSize({ w: clampedSizeW, h: clampedSizeH });
    }
    setVw(vwNow);
    setVh(vhNow); // ★ v6.80: 保存视口高度,用于 default size 自适应
    setHydrated(true);
  }, []);

  // 保存 localStorage (拖动/resize 中持续保存)
  useEffect(() => {
    if (!hydrated) return; // 避免初始 state 覆盖 localStorage
    try {
      localStorage.setItem('dongniai_sidebar_geom', JSON.stringify({ pos, size }));
    } catch {}
  }, [pos, size, hydrated]);

  // 【v6.97.147】保存折叠状态 (hydration 后才生效, 避免 SSR 误覆盖)
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem('dongniai_sidebar_collapsed', collapsed ? 'true' : 'false');
    } catch {}
  }, [collapsed, hydrated]);

  // ★ v6.79/v6.80: 拖动范围 — 允许 sidebar 在屏幕内任意拖, 只要求至少 MIN_VISIBLE_PX 在视口内
  //   v6.79 公式 (旧): 基于 right:0, top:50% (sidebar 贴右+垂直居中)
  //   v6.80 公式 (新): 基于 right:SIDE_MARGIN, top:TOP_OFFSET (sidebar 距右 20px + 顶部 64px)
  //   默认起点: right:SIDE_MARGIN → sidebar.right = vw - SIDE_MARGIN
  //             top:TOP_OFFSET → sidebar.top = TOP_OFFSET
  //   transform(x,y) = 整体偏移, 所以 sidebar.right = vw - SIDE_MARGIN + x, sidebar.left = vw - SIDE_MARGIN + x - w
  //                              sidebar.top = TOP_OFFSET + y, sidebar.bottom = TOP_OFFSET + y + h
  function clampPos(p: { x: number; y: number }, w: number, h: number, vw: number, vh: number) {
    const M = MIN_VISIBLE_PX;
    // X 方向: sidebar.right >= M (往左极限) AND sidebar.left <= vw - M (往右极限)
    const minX = SIDE_MARGIN - vw + M;     // v6.80: sidebar 默认 right = vw - SIDE_MARGIN
    const maxX = w + SIDE_MARGIN - M;      // 让 sidebar.left 最大 vw - M
    // Y 方向: sidebar.bottom >= M (往上极限) AND sidebar.top <= vh - M (往下极限)
    const minY = M - TOP_OFFSET - h;       // sidebar 默认 top = TOP_OFFSET
    const maxY = vh - M - TOP_OFFSET;
    return {
      x: Math.max(minX, Math.min(maxX, p.x)),
      y: Math.max(minY, Math.min(maxY, p.y)),
    };
  }

  // 拖动整卡 (卡片头部是 drag handle)
  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const startMouseX = e.clientX;
    const startMouseY = e.clientY;
    const startPosX = pos.x;
    const startPosY = pos.y;
    // 实时获取当前渲染高度 (height 是动态的, 用户 resize 后变化)
    const target = e.currentTarget.parentElement as HTMLElement | null;
    const currentH = target?.offsetHeight || DEFAULT_H; // ★ v6.80 fallback 改 600
    const handleMove = (ev: MouseEvent) => {
      const deltaX = ev.clientX - startMouseX;
      const deltaY = ev.clientY - startMouseY;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const currentW = size.w || (vw >= 1280 ? DEFAULT_W_DESKTOP : DEFAULT_W_MOBILE); // ★ v6.80
      // ★ v6.73 用统一 clampPos 函数 (保证至少 60px 在屏)
      const clamped = clampPos({ x: startPosX + deltaX, y: startPosY + deltaY }, currentW, currentH, vw, vh);
      setPos(clamped);
    };
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.cursor = '';
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.body.style.cursor = 'grabbing';
  };

  // 拉宽/拉长 (右下角 resize handle) — 大厂级体验
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    // ★ 用实际渲染尺寸 (offsetWidth/Height) 而不是 state.size.w (可能是 0 = 默认值)
    const target = e.currentTarget.parentElement as HTMLElement | null;
    const startW = target?.offsetWidth ?? currentW;
    const startH = target?.offsetHeight ?? 480;
    setResizing(true);
    setResizeHint(`${Math.round(startW)} × ${Math.round(startH)}`);
    const handleMove = (ev: MouseEvent) => {
      const deltaX = ev.clientX - startX;
      const deltaY = ev.clientY - startY;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // ★ v6.73: 最小尺寸 280×360,最大 = 视口 - 40
      const newW = Math.max(MIN_W, Math.min(vw - 40, startW + deltaX));
      const newH = Math.max(MIN_H, Math.min(vh - 40, startH + deltaY));
      setSize({ w: newW, h: newH });
      // ★ v6.73: resize 后 clamp 位置 (防止缩小后 sidebar 飞出视口)
      const finalH = newH;
      setPos((p) => clampPos(p, newW, finalH, vw, vh));
      setResizeHint(`${Math.round(newW)} × ${Math.round(newH)}`);
    };
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.cursor = '';
      setResizing(false);
      // 1.5s 后清除尺寸提示
      setTimeout(() => setResizeHint(''), 1500);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.body.style.cursor = 'nwse-resize';
  };

  // ★ 大厂做法: 4 边 + 4 角 都是 resize 热区 (鼠标悬停时高亮边缘)
  const handleEdgeResize = (e: React.MouseEvent, edge: string) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const target = e.currentTarget.parentElement?.parentElement as HTMLElement | null;
    const startW = target?.offsetWidth ?? currentW;
    const startH = target?.offsetHeight ?? 480;
    setResizing(true);
    setResizeHint(`${Math.round(startW)} × ${Math.round(startH)}`);
    const handleMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let newW = startW, newH = startH, posDx = 0, posDy = 0;
      if (edge.includes('e')) newW = startW + dx;
      if (edge.includes('w')) { newW = startW - dx; posDx = dx; }
      if (edge.includes('s')) newH = startH + dy;
      if (edge.includes('n')) { newH = startH - dy; posDy = dy; }
      // ★ v6.73: 最小 280×360,最大 视口 - 40
      newW = Math.max(MIN_W, Math.min(vw - 40, newW));
      newH = Math.max(MIN_H, Math.min(vh - 40, newH));
      setSize({ w: newW, h: newH });
      if (posDx !== 0 || posDy !== 0) {
        // ★ v6.73: 拖 w/n 边时,sidebar 左边/顶边跟手,但 resize 后整体 clamp 到视口
        setPos((p) => {
          const adjusted = { x: p.x + posDx, y: p.y + posDy };
          return clampPos(adjusted, newW, newH, vw, vh);
        });
      } else {
        // ★ v6.73: e/s 边拖动也 clamp pos (sidebar 缩小可能飞出)
        setPos((p) => clampPos(p, newW, newH, vw, vh));
      }
      setResizeHint(`${Math.round(newW)} × ${Math.round(newH)}`);
    };
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.cursor = '';
      setResizing(false);
      setTimeout(() => setResizeHint(''), 1500);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    const cursors: Record<string, string> = {
      e: 'ew-resize', w: 'ew-resize', s: 'ns-resize', n: 'ns-resize',
      se: 'nwse-resize', sw: 'nesw-resize', ne: 'nesw-resize', nw: 'nwse-resize',
    };
    document.body.style.cursor = cursors[edge] || 'nwse-resize';
  };

  // ★ 双击 reset = 大厂标配 (Figma/Notion/Linear 都有)
  const handleReset = () => {
    setPos({ x: 0, y: 0 });
    setSize({ w: 0, h: 0 });
    setIsFullscreen(false);
  };

  // ★ 双击放大镜 = 一键全屏 (老板演示/教学场景)
  const toggleFullscreen = () => {
    if (isFullscreen) {
      handleReset();
    } else {
      setPos({ x: 0, y: 0 });
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setSize({ w: Math.min(900, vw - 80), h: Math.min(800, vh - 80) });
      setIsFullscreen(true);
    }
  };

  // 计算 inline style (default position 偏移 + 自定义尺寸)
  // ★ v6.80: 默认 size 改 300×600 (大厂风, 内容超过时 Card 内部滚动 - v6.78 已修)
  const defaultW = vw >= 1280 ? DEFAULT_W_DESKTOP : DEFAULT_W_MOBILE;
  const currentW = Math.max(MIN_W, size.w || defaultW);
  // ★ v6.80: 默认 height 自适应视口 — 大屏幕 600, 小屏幕自动缩小, 永不超出 vh - 84
  const vhAvail = vh - TOP_OFFSET - SIDE_MARGIN;
  const defaultH = Math.max(MIN_H, Math.min(DEFAULT_H, vhAvail));
  const currentH = size.h === 0 ? defaultH : Math.max(MIN_H, size.h);

  // ★ v6.97.147 (2026-09-17 老板需求): collapsed 时只渲染右下角悬浮按钮, 隐藏整个 sidebar
  //   老板原话: "管理课程和近期上课课程的卡片要可以点击展开/折叠(折叠变成右下角的悬浮按钮, 默认为展开状态)"
  //   - FAB 设计参考 Linear/Notion: 圆形 + 紫色阴影 + hover 微缩放
  //   - 图标: Library (课程库语义清晰)
  //   - 位置: fixed bottom-6 right-6 (右下角, 跟 sidebar 默认位置呼应)
  //   - z-50 高于 sidebar (z-20), 确保折叠态按钮始终在最上层
  if (collapsed) {
    return (
      <motion.button
        type="button"
        aria-label="展开课程卡片"
        title="展开课程卡片"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setCollapsed(false)}
        // 使用 hidden lg:flex 跟原 sidebar 一致, 移动端不显示
        className="hidden lg:flex"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: 'max(24px, env(safe-area-inset-right, 24px))',
          zIndex: 50,
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgb(139 92 246) 0%, rgb(124 58 237) 100%)', // 紫色渐变 (跟 sidebar 主题一致)
          color: '#fff',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          cursor: 'pointer',
          // 大厂级阴影: 浮起感 + 紫色光晕
          boxShadow: '0 8px 24px rgba(139, 92, 246, 0.35), 0 0 0 1px rgba(139, 92, 246, 0.15), 0 2px 6px rgba(0,0,0,0.08)',
        }}
      >
        <Library className="size-5" strokeWidth={2.2} />
        {/* 右上角小红点提醒 (跟已有 PlanPopup 提醒样式一致, 标识"还有课程") */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: '6px',
            right: '6px',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#ef4444',
            boxShadow: '0 0 0 2px #fff',
          }}
        />
      </motion.button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        // ★ v6.80 (老板反馈 "默认位置和大小像大厂"): 顶部 64px + 距右 20px, 不贴边垂直居中
        top: `${TOP_OFFSET}px`,
        // 【v6.97.62+2 P0 修复 2026-08-03 老板报 Mac Safari 1440 viewport 显示问题】
        //   真因: Chromium fixed 元素 right 锚点用 layout viewport (不含 scrollbar),
        //   而不是 visual viewport. scrollbar gutter 15px 让 loginCard 实际位置
        //   x = 1425 - 20 - 300 = 1105, 而 mainCard.right = 1113 (居中 1440 viewport),
        //   重叠 8px. Windows 抗锯齿模糊掉, Mac 像素精确所以老板明显看到.
        //   修法: right 改成 max(24px, env(safe-area-inset-right, 24px)),
        //   保证 ≥24px 右边距 + iOS PWA 模式 notch 适配.
        right: `max(24px, env(safe-area-inset-right, 24px))`,
        // ★ 改用 translate(x,y), 不再用 translate(-50%) (因为 top 是 px 不是 %)
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        width: currentW,
        height: currentH,
        maxHeight: `calc(100vh - ${TOP_OFFSET + SIDE_MARGIN}px)`, // 顶部 64 + 底部 20 margin
        // 【v6.97.62+4 P0 修复 2026-08-03】⚠️ 不能在这里写 display:'flex',
        //   inline style 永远高于 CSS class, 会让 hidden 失效!
        //   把 display/flexDirection 移到 className, 让 Tailwind breakpoint
        //   能真正控制 sidebar 显隐. min-[1536px]:flex 在 ≥1536px 才生效
        flexDirection: 'column',
        zIndex: 20,
        // ★ v6.80: 大厂双层阴影 + 圆角 (像 Linear/Notion/Figma 浮窗 sidebar)
        borderRadius: '16px',
        boxShadow: '0 12px 48px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.05)',
        overflow: 'hidden', // 让 Card 圆角被裁剪, 视觉一致
        outline: resizing ? '2px dashed rgb(139 92 246)' : 'none',
        outlineOffset: '2px',
        transition: resizing ? 'none' : 'outline 0.2s, box-shadow 0.2s',
      }}
      className="hidden lg:flex course-catalog-sidebar"
    >
      {/* ★ v6.77 (2026-07-12 老板反馈): 卡片拉长后底部空白
          - 之前 inner 用 flex:1 + overflow:auto 让 sidebar 高度 = 内容高度, Card 高度未跟父级
          - 现在 inner 改 display:flex + flexDirection:column, Card 用 flex:1 撑开父级
          - handle 仍在 inner 内 (position: absolute 相对 inner) */}
      <div
        onMouseDown={handleDragStart}
        style={{ cursor: 'grab', position: 'relative', flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <CourseCatalogCard />

        {/* ★ v6.76 (2026-07-12 老板反馈): 只保留右下角调整大小
            去掉 左/右/下 三边热区 (老板觉得多余), 只保留右下角大 handle */}

        {/* 右下角大 handle (20×20, 紫色边框 + 圆点, 内缩 4px 让它明显在框内) */}
        <div
          onMouseDown={(e) => handleEdgeResize(e, 'se')}
          className="absolute z-20 cursor-nwse-resize"
          style={{
            bottom: '4px', right: '4px',  // ★ v6.75 内缩 4px 让 handle 明显在 sidebar 内
            width: '20px', height: '20px',
            background: 'linear-gradient(135deg, transparent 35%, rgba(139,92,246,0.95) 35%)',
            border: '1px solid rgba(255,255,255,0.6)', // ★ 白色边框保证 handle 在任何背景都清晰
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',   // ★ 阴影让 handle 浮起来,跟 sidebar 边缘分层
            transition: 'transform 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.4)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          title="拖动调整大小 · 双击恢复默认"
          onDoubleClick={(e) => { e.stopPropagation(); handleReset(); }}
        />

        {/* ★ v6.97.147 (2026-09-17 老板需求) 折叠按钮 — 常驻显示 (老板原话"可以点击", 不能只在 hover 才显)
            位置: 右上角, 工具按钮组左边, 永远 opacity-100
            图标: PanelRightClose 表示"折叠到右边" */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setCollapsed(true); }}
          onMouseDown={(e) => e.stopPropagation()}
          title="折叠到右下角"
          aria-label="折叠到右下角"
          className="absolute top-2 right-2 z-30 w-6 h-6 rounded flex items-center justify-center bg-white/90 hover:bg-violet-500 hover:text-white text-gray-600 shadow-sm border border-gray-200"
        >
          <PanelRightClose className="size-3.5" />
        </button>

        {/* 3) 工具按钮组 (悬停卡片时显示, 右上角: 全屏 + 重置)
            注: 折叠按钮已拆出来单独常驻, 这个组只剩全屏 + 重置, 位置从 right-2 → right-10 */}
        <div
          className="absolute top-2 right-10 z-30 flex gap-1 opacity-0 hover:opacity-100 transition-opacity"
          style={{ pointerEvents: 'auto' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* 放大镜/全屏 — 位置 right-10 给折叠按钮让位 (v6.97.147) */}
          <button
            type="button"
            onClick={toggleFullscreen}
            onMouseDown={(e) => e.stopPropagation()}
            title={isFullscreen ? '退出全屏' : '放大到合适大小'}
            className="w-6 h-6 rounded flex items-center justify-center bg-white/90 hover:bg-violet-500 hover:text-white text-gray-600 shadow-sm border border-gray-200"
            style={{ fontSize: '12px', lineHeight: 1 }}
          >
            {isFullscreen ? '⛶' : '⛶'}
          </button>
          {/* 重置按钮 */}
          <button
            type="button"
            onClick={handleReset}
            onMouseDown={(e) => e.stopPropagation()}
            title="恢复默认位置和大小"
            className="w-6 h-6 rounded flex items-center justify-center bg-white/90 hover:bg-violet-500 hover:text-white text-gray-600 shadow-sm border border-gray-200"
            style={{ fontSize: '11px', lineHeight: 1 }}
          >
            ↺
          </button>
        </div>

        {/* 4) resize 中实时尺寸 tooltip (显示在卡片底部中央) */}
        {resizeHint && (
          <div
            className="absolute left-1/2 z-40 pointer-events-none"
            style={{
              bottom: '-32px',
              transform: 'translateX(-50%)',
              background: 'rgba(17,24,39,0.95)',
              color: 'white',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              whiteSpace: 'nowrap',
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            }}
          >
            {resizeHint} px
          </div>
        )}
      </div>
    </div>
  );
}


const RECENT_OPEN_STORAGE_KEY = 'recentClassroomsOpen';
const INTERACTIVE_MODE_STORAGE_KEY = 'interactiveModeEnabled';

interface FormState {
  pdfFile: File | null;
  /** 2026-07-29 多文件上传 additive】镜像官方 OpenMAIC 多文件上传
   * 官方 page.tsx 已移除 pdfFile 单文件 API; prod 保留 pdfFile 向后兼容 (cast mime 流程),
   * 同步接 SelectedCourseMaterial[] 多文件 API (与 toolbar 的 handleFilesSelect 对接) */
  courseMaterials: SelectedCourseMaterial[];
  requirement: string;
  interactiveMode: boolean;
  /** 2026-06-08 上传的图片列表(本地 base64 + 元信息) */
  uploadedImages: UploadedImage[];
}

const initialFormState: FormState = {
  pdfFile: null,
  courseMaterials: [],
  requirement: '',
  interactiveMode: false,
  uploadedImages: [],
};

/**
 * 给跳转 URL 附加 ?return_url= 当前页（用于充值/购买套餐后跳回）
 * 2026-06-04: 修复「充值完成跳到账单页而非原页面」bug
 * 后端 Recharge.php 会校验 host 白名单，外站/危险 URL 自动兜底为账单页
 */
function buildReturnUrl(target: string): string {
  if (typeof window === 'undefined') return target;
  if (!target) return target;
  if (/[?&]return_url=/.test(target)) return target;
  const sep = target.includes('?') ? '&' : '?';
  return `${target}${sep}return_url=${encodeURIComponent(window.location.pathname + window.location.search)}`;
}

function HomePage() {
  // Per spec: continuously sync admin defaults so backend changes (provider
  // priorities, role TTS voices, model selection, etc.) take effect in the
  // running client without a manual page reload.
  useConfigVersionPolling();
  const { t } = useI18n();
  const { theme, setTheme } = useTheme();
  const router = useRouter();
  const [form, setForm] = useState<FormState>(initialFormState);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState<
    import('@/lib/types/settings').SettingsSection | undefined
  >(undefined);
  const [planPopupOpen, setPlanPopupOpen] = useState(false);
  const [planPopupPermanentDismiss, setPlanPopupPermanentDismiss] = useState(false);
  const [sessionDismissed, setSessionDismissed] = useState(
    typeof window !== 'undefined' ? sessionStorage.getItem('plan_popup_session_dismissed') === 'true' : false
  );
  const [planPopupClosing, setPlanPopupClosing] = useState(false); // shrink animation in progress
  const [planBtnRect, setPlanBtnRect] = useState<DOMRect | null>(null);
  const [recentOpen, setRecentOpen] = useState(true);
  // Admin-controlled deep interaction switch (loaded from database, true=allowed, false=hidden)
  const [adminInteractiveEnabled, setAdminInteractiveEnabled] = useState<boolean | null>(null);
  // 是否有管理课程（用于控制弹窗是否显示）
  const [hasManagementCourses, setHasManagementCourses] = useState(false);
  // 弹窗抑制状态（来自服务端，跨设备）
  const [popupServerPreference, setPopupServerPreference] = useState<{
    no_remind: number;
    no_remind_at: number;
    chose_to_plan: number;
    chose_to_plan_at: number;
    dismissed_30days_at: number;
  } | null>(null);
  // 加载弹窗偏好中
  const [popupPrefLoading, setPopupPrefLoading] = useState(false);

  // Draft cache for requirement text
  const { cachedValue: cachedRequirement, updateCache: updateRequirementCache } =
    useDraftCache<string>({ key: 'requirementDraft' });

  // Load admin defaults at page mount so all services are pre-configured.
  // User does NOT need to open Settings — all defaults come from the backend.
  // MUST await so the toolbar renders AFTER the store is updated (not before).
  useEffect(() => {
    void useSettingsStore.getState().fetchAdminDefaults();
  }, []);

  // 弹窗抑制条件：返回 true 表示应该抑制（不弹窗）
  // [REUSE] onFocus 跟初始 useEffect 都用这个函数，避免逻辑漂移
  // [BUG-FIX] 之前 onFocus 内部嵌套了 9 个判断，新 useEffect 容易漏条件
  // [WHY-EXTRACT] 用户报告"后续还是弹窗"，根因之一是同一段逻辑在多处重复
  const shouldSuppressPlanPopup = (): boolean => {
    // 还没加载完管理课程状态 → 暂不弹（等加载完）
    if (!popupPrefLoading && hasManagementCourses) return true;
    // 服务端永久不再提醒
    if (popupServerPreference?.no_remind === 1) return true;
    // 用户已选择过AI规划
    if (popupServerPreference?.chose_to_plan === 1) return true;
    // 30天抑制（服务端时间戳，跨设备）
    if (popupServerPreference?.dismissed_30days_at && popupServerPreference.dismissed_30days_at > 0) {
      const daysSince = (Date.now() - popupServerPreference.dismissed_30days_at) / (1000 * 60 * 60 * 24);
      if (daysSince < 30) return true;
    }
    // 本地永久不再提醒（兼容旧数据）
    try { if (localStorage.getItem('plan_popup_dismissed_forever')) return true; } catch {}
    // 本地30天抑制（兼容旧数据）
    try {
      const dismissedAt = localStorage.getItem('plan_popup_dismissed_30days');
      if (dismissedAt) {
        const daysSince = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
        if (daysSince < 30) return true;
      }
    } catch {}
    // session级别抑制（用户本会话已点过关闭）
    if (sessionDismissed) return true;
    return false;
  };

  // [2026-06-25 修复 P0-UX] 禁用组件挂载后 3 秒自动弹窗
  // 真因: 自动弹窗覆盖整个屏幕，拦截用户点导航栏"登录/价格/下载"按钮
  //       → 老板报"点登录没反应"+"流程走不通"+"计时器 00:00:00" 的真凶之一
  // 修法: 移除自动弹窗 useEffect；只保留 textarea onFocus 手动触发（用户明确意图才弹）
  // 保留 shouldSuppressPlanPopup() / popupPrefLoading / popupServerPreference 状态供手动触发复用
  useEffect(() => {
    // 故意留空：已禁用自动弹窗
  }, []);

  // 2026-07-29 试卷底部"生成专属课堂"触发：从 URL query + localStorage 读 prompt → 注入 form → 自动 handleGenerate
  // 2026-07-31 老板 P0："根据错题/收藏题生成专属课堂" 加 fill_only=1 参数：只填输入框，不自动点"进入课堂"
  //   - 错题/收藏题场景：prompt 比较长，老板需要先看 prompt 内容再决定是否生成
  //   - 所以加 fill_only 参数：不自动 handleGenerate，只 setForm.requirement
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('autostart_personalized') === '1') {
      try {
        const pendingPrompt = localStorage.getItem('dongniai_pending_personalized_prompt');
        if (pendingPrompt) {
          setForm((prev) => ({ ...prev, requirement: pendingPrompt }));
          updateRequirementCache(pendingPrompt);
          localStorage.removeItem('dongniai_pending_personalized_prompt');
          // 清理 URL（同时清掉 fill_only）
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
          // 【2026-07-31 P0】fill_only=1 时只填输入框，不自动点"进入课堂"
          const fillOnly = params.get('fill_only') === '1';
          if (!fillOnly) {
            // 默认行为：自动点"进入课堂"按钮（保持试卷底部"生成专属课堂"的旧逻辑）
            // ★ v6.97.86 P0 修复: 用 helper 替代原 setTimeout(800) + `button[title*="进入课堂"]` (该 selector 100% 找不到, 因生产 button 没 title 属性)
            tryAutoClickEnterClassroom({ timeoutMs: 3000, intervalMs: 50 });
          }
        }
      } catch { /* ignore */ }
    }
  }, []);

  // 加载弹窗偏好（跨设备"不再提醒"）和管理课程状态
  useEffect(() => {
    // 并行加载：弹窗偏好 + 管理课程
    const loadPref = async () => {
      setPopupPrefLoading(true);
      try {
        const res = await fetch('/dongniai_public.php?method=planPopupPreference', {
          credentials: 'include',
        });
        if (res.ok) {
          const json = await res.json();
          if (json.success && json.data) {
            setPopupServerPreference(json.data);
          }
        }
      } catch { /* ignore */ } finally {
        setPopupPrefLoading(false);
      }
    };

    // 检查管理课程：调用 myForms API
    const loadForms = async () => {
      try {
        const res = await fetch('/dongniai_public.php?method=myForms', { credentials: 'include' });
        if (res.ok) {
          const json = await res.json();
          setHasManagementCourses(json.code === 1 && Array.isArray(json.data?.forms) && json.data.forms.length > 0);
        }
      } catch { /* ignore */ }
    };

    loadPref();
    loadForms();
  }, []);
  const persistRecentOpen = (next: boolean) => {
    setRecentOpen(next);
    try {
      localStorage.setItem(RECENT_OPEN_STORAGE_KEY, String(next));
    } catch {
      /* ignore */
    }
  };

  // Hydrate client-only state after mount (avoids SSR mismatch)
  /* eslint-disable react-hooks/set-state-in-effect -- Hydration from localStorage must happen in effect */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(RECENT_OPEN_STORAGE_KEY);
      if (saved !== null) setRecentOpen(saved !== 'false');
    } catch {
      /* localStorage unavailable */
    }
    try {
      const savedInteractiveMode = localStorage.getItem(INTERACTIVE_MODE_STORAGE_KEY);
      const updates: Partial<FormState> = {};
      if (savedInteractiveMode === 'true') updates.interactiveMode = true;
      if (Object.keys(updates).length > 0) {
        setForm((prev) => ({ ...prev, ...updates }));
      }
    } catch {
      /* localStorage unavailable */
    }
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Sync FastAdmin login status on mount (reads token cookie → /api/auth/status)
  useEffect(() => {
    useAuthStore.getState().fetchStatus();
  }, []);

  // Load admin feature switch for deep interaction (runs once after mount)
  useEffect(() => {
    let cancelled = false;
    fetch('/api/admin/feature-status', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d: { success: boolean; data: Record<string, { status: number }> }) => {
        if (cancelled) return;
        const deepInteraction = d?.data?.interactive;
        setAdminInteractiveEnabled(deepInteraction && deepInteraction.status === 1);
      })
      .catch(() => {
        if (cancelled) return;
        setAdminInteractiveEnabled(true); // fail-open: allow if API unreachable
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Pick up lesson click from CourseCatalogCard (localStorage signal)
  // ★ 从 localStorage 恢复提示词后也自动点击"进入课堂"
  useEffect(() => {
    const pending = localStorage.getItem('pendingRequirement');
    if (pending) {
      updateForm('requirement', pending);
      localStorage.removeItem('pendingRequirement');
      // ★ v6.97.86 P0 修复: 用 helper 替代原 setTimeout(800) + forEach (selector 行为不一致, 且固定延迟漏按钮)
      tryAutoClickEnterClassroom({ timeoutMs: 3000, intervalMs: 50 });
    }
    const params = new URLSearchParams(window.location.search);
    // ★ 2026-06-08: 从 myForms 等页面带 ?outline_id=X&lesson=Y 跳过来时，
    //    调 buildLessonPrompt API 拉 prompt，存到 localStorage 后续和 handleFillPrompt 共用路径
    //    （清理 URL 的代码保留，避免刷新时再次触发）
    if (params.has('outline_id') && params.has('lesson')) {
      const outlineId = parseInt(params.get('outline_id') || '0', 10);
      const lessonIndex = parseInt(params.get('lesson') || '0', 10);
      // 先清理 URL（防止刷新时再次触发；同时也避免别人看到这些参数）
      params.delete('outline_id');
      params.delete('lesson');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);
      // 异步调 API 拉 prompt
      if (outlineId && lessonIndex) {
        (async () => {
          try {
            const res = await fetch('/dongniai_plan.php?api=buildLessonPrompt', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ outline_id: outlineId, lesson_index: lessonIndex }),
            });
            const json = await res.json();
            if (json?.code === 1 && json.data?.prompt) {
              // 复用 fill-course-prompt 事件，让 page.tsx 走统一的"自动填词+点进入课堂"流程
              window.dispatchEvent(new CustomEvent('fill-course-prompt', {
                detail: { prompt: json.data.prompt, outlineId, lessonIndex },
                bubbles: true,
              }));
            } else {
              log.warn('[URL deep-link] buildLessonPrompt failed:', json?.msg);
            }
          } catch (err) {
            log.warn('[URL deep-link] buildLessonPrompt request failed:', err);
          }
        })();
      }
    } else if (params.has('outline_id') || params.has('lesson')) {
      // 兜底：只有 outline_id 或只有 lesson 也清理掉 URL
      params.delete('outline_id');
      params.delete('lesson');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
    // Listen for "fill course prompt" events from CourseCatalogCard
    // ★ 填入提示词后自动点击"进入课堂"，省去用户手动操作
    // ★ 同时存 outlineId + lessonIndex 到 localStorage，供 generation-preview 创建课堂后回写 lesson_progress
    const handleFillPrompt = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.prompt) {
        updateForm('requirement', detail.prompt);
        // ★ 存课程关联信息，generation-preview 完成后会读取并回写 lesson_progress
        if (detail.outlineId != null && detail.lessonIndex != null) {
          try {
            localStorage.setItem('dongniai_lesson_link', JSON.stringify({
              outlineId: detail.outlineId,
              lessonIndex: detail.lessonIndex,
            }));
          } catch { /* ignore */ }
        }
        textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textareaRef.current?.focus();
        // ★ v6.97.86 P0 修复: 用 helper 替代原 setTimeout(600) + 多 button 扫描
        //   原问题: 固定 600ms 延迟, React re-render 慢时按钮 disabled=true, btn.click() 漏触发
        //   新版: 循环重试 50ms/次 3s 超时, 等按钮 enabled 后才 click
        tryAutoClickEnterClassroom({ timeoutMs: 3000, intervalMs: 50 });
      }
    };
    window.addEventListener('fill-course-prompt', handleFillPrompt);
    return () => window.removeEventListener('fill-course-prompt', handleFillPrompt);
  }, []);

  // Restore requirement draft from cache (derived state pattern — no effect needed)
  const [prevCachedRequirement, setPrevCachedRequirement] = useState(cachedRequirement);
  if (cachedRequirement !== prevCachedRequirement) {
    setPrevCachedRequirement(cachedRequirement);
    if (cachedRequirement) {
      setForm((prev) => ({ ...prev, requirement: cachedRequirement }));
    }
  }

  const [themeOpen, setThemeOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [webSearch, setWebSearch] = useState(false);
  // 【2026-06-08】点击"进入课堂"时如果带图片，会先调 OCR。此 state 控制全屏覆盖
  const [ocrOverlay, setOcrOverlay] = useState<{ status: 'running' | 'done'; message: string } | null>(null);
  // 【2026-06-03 课时核心理则】点击"进入课堂"时的课时预检结果。
  // null = 未触发预检失败；object = 后端 doBalanceCheck 返回的失败信息。
  // 结构与后端 canAffordOneMinute 返回对齐：
  //   - source: 'negative_balance' | 'balance' | 其他
  //   - message: 失败原因（用户可读的完整文案）
  //   - rechargeUrl / planUrl: 前端按钮跳转链接
  const [balanceError, setBalanceError] = useState<{
    message: string;
    source: string;
    balance: number;
    rechargeUrl: string;
    planUrl: string;
  } | null>(null);
  const [classrooms, setClassrooms] = useState<StageListItem[]>([]);
  const [thumbnails, setThumbnails] = useState<Record<string, Slide>>({});
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (!themeOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        setThemeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [themeOpen]);

  const loadClassrooms = async () => {
    try {
      const list = await listStages();
      setClassrooms(list);
      // Load first slide thumbnails
      if (list.length > 0) {
        const slides = await getFirstSlideByStages(list.map((c) => c.id));
        setThumbnails(slides);
      }
      // ★ 第一波·IndexedDB 兜底：如果本地为空，从服务端拉云端课堂元数据
      //   目的：换浏览器/清缓存后，"最近学习"仍能展示历史课堂
      //   限制：服务端只存 meta（name/desc/sceneCount），不存 scenes
      //         → 点击后 classroom 页会从 /api/classroom?id=xxx 拉全量 scene 写回 IndexedDB
      if (list.length === 0) {
        try {
          const res = await fetch('/dongniai_plan.php?api=getMyClassrooms', { credentials: 'include' });
          const json = await res.json();
          if (json.code === 1 && Array.isArray(json.data?.classrooms)) {
            // 把服务端 meta 写回 IndexedDB（只写 meta 字段，scenes 不写）
            // 这样后续"删除"等操作能正常命中本地记录
            // 【v6.48.2 batch 优化 2026-07-11】老板反馈"打开慢"
            //   原 100 次串行 saveStageMetaOnly + 100 条 console.log 改为 1 次 bulkPut
            //   收益：IndexedDB 卡 200-500ms → <50ms，console 100 条 → 1 条
            //   修因：v6.46 9ac05689 引入 saveStageMetaOnly 串行循环让首页首次加载卡 500ms
            try {
              await saveStageMetaOnlyBatch(json.data.classrooms);
            } catch (writeErr) {
              log.warn('[loadClassrooms] saveStageMetaOnlyBatch failed:', writeErr);
            }
            setClassrooms(json.data.classrooms);
            log.info('[loadClassrooms] IndexedDB empty, recovered', json.data.classrooms.length, 'classrooms from server');
          }
        } catch (serverErr) {
          log.warn('[loadClassrooms] server fallback failed (non-blocking):', serverErr);
        }
      }
    } catch (err) {
      log.error('Failed to load classrooms:', err);
    }
  };

  const { importing, fileInputRef, triggerFileSelect, handleFileChange } = useImportClassroom(
    () => {
      loadClassrooms();
    },
  );

  useEffect(() => {
    // Clear stale media store to prevent cross-course thumbnail contamination.
    // The store may hold tasks from a previously visited classroom whose elementIds
    // (gen_img_1, etc.) collide with other courses' placeholders.
    useMediaGenerationStore.getState().revokeObjectUrls();
    useMediaGenerationStore.setState({ tasks: {} });

    // eslint-disable-next-line react-hooks/set-state-in-effect -- Store hydration on mount
    loadClassrooms();
  }, []);

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingDeleteId(id);
  };

  const confirmDelete = async (id: string) => {
    setPendingDeleteId(null);
    try {
      await deleteStageData(id);
      await loadClassrooms();
    } catch (err) {
      log.error('Failed to delete classroom:', err);
      toast.error('Failed to delete classroom');
    }
  };

  const handleRename = async (id: string, newName: string) => {
    try {
      await renameStage(id, newName);
      setClassrooms((prev) => prev.map((c) => (c.id === id ? { ...c, name: newName } : c)));
    } catch (err) {
      log.error('Failed to rename classroom:', err);
      toast.error(t('classroom.renameFailed'));
    }
  };

  const deferredSearchQuery = useDeferredValue(searchQuery);
  const filteredClassrooms = useMemo(() => {
    const q = deferredSearchQuery.trim().toLowerCase();
    if (!q) return classrooms;
    return classrooms.filter((c) => {
      const name = c.name?.toLowerCase() ?? '';
      const desc = c.description?.toLowerCase() ?? '';
      return name.includes(q) || desc.includes(q);
    });
  }, [classrooms, deferredSearchQuery]);

  const updateForm = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    try {
      if (field === 'interactiveMode')
        localStorage.setItem(INTERACTIVE_MODE_STORAGE_KEY, String(value));
      if (field === 'requirement') updateRequirementCache(value as string);
    } catch {
      /* ignore */
    }
  };

  // 【2026-07-29 多文件上传 additive】镜像官方 OpenMAIC 多文件上传 handler
  // 官方 page.tsx:287-314 addCourseMaterials + removeCourseMaterial
  // prod 同时保留 pdfFile 单文件 API 向后兼容 (cast mime 流程未变, 见 page.tsx:1088-1091)
  const addCourseMaterials = (files: File[]) => {
    setForm((prev) => {
      const dedupedFiles = dedupeCourseMaterialFiles(prev.courseMaterials, files);
      const startOrder = prev.courseMaterials.length + 1;
      const additions = dedupedFiles.map((file, index) => ({
        id: nanoid(8),
        file,
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
        type: file.type,
        order: startOrder + index,
      }));

      return additions.length > 0
        ? { ...prev, courseMaterials: [...prev.courseMaterials, ...additions] }
        : prev;
    });
  };

  const removeCourseMaterial = (id: string) => {
    setForm((prev) => ({
      ...prev,
      courseMaterials: prev.courseMaterials
        .filter((item) => item.id !== id)
        .map((item, index) => ({ ...item, order: index + 1 })),
    }));
  };

  const handleGenerate = async () => {
    // 【v6.97.166.4 P0 修复 2026-09-21 老板报】exercise review "根据作答情况生成专属课堂" 按钮点击后
    //   - 真因: page mount 时 useAuthStore.fetchStatus() (line 845) 异步触发, 但 tryAutoClickEnterClassroom (line 771) 同步 click "进入课堂"
    //           → handleGenerate 同步读 useAuthStore.getState().loggedIn 时 fetchStatus 还没完成
    //           → loggedIn 还是初始值 false → if (!loggedIn) 命中 → 跳 login.html
    //           → 用户看到 "您已登录,请勿重复登录" 提示 + 跳到会员中心, 看似"没生成专属课堂"
    //   - 修法: 在 if (!loggedIn) 之前 await fetchStatus() 让 in-flight 的 auth 检查先完成
    //           - 已登录场景: fetchStatus 完成后 loggedIn=true → 跳过 if 块, 走 handleGenerate 后续流程
    //           - 真实未登录场景: fetchStatus 完成后 loggedIn=false → 才跳 login.html (既有防御保留)
    //   - 影响面: 仅修改 handleGenerate 第一段 auth 检查, 不影响 handleGenerate 后续 balance_check + session_create 等逻辑
    await useAuthStore.getState().fetchStatus();
    const { loggedIn } = useAuthStore.getState();
    if (!loggedIn) {
      const returnUrl = encodeURIComponent(window.location.href);
      // 【v6.97.83+5 P0 修复 2026-09-01 老板报】相对路径 /index/user/login.html 在 basePath=/dongniai 下
      //   可能被 Next.js 客户端 router / nginx 兜底重写为 /dongniai/index/user/login.html (404)
      //   按 v6.97.67 模板统一升级为 window.location.origin + 绝对路径, 绕开 basePath 重写
      window.location.href = window.location.origin + `/index/user/login.html?url=${returnUrl}`;
      return;
    }

    // modelId is pre-loaded from admin defaults at page mount — no user setup needed
    const hasText = !!form.requirement.trim();
    const doneImages = form.uploadedImages.filter((i) => i.status === 'done');
    if (!hasText && doneImages.length === 0) {
      setError(t('upload.requirementRequired'));
      return;
    }

    setError(null);

    // 关键修复：只生成一个 roomId（之前生成 tempRoomId + sessionState.roomId 两个不同 nanoid，
    // 如果 generation-preview 加载时还在 temp 阶段就读到错的 ID，导致后续切到 classroom 时
    // doEnter 拿不同 room_id 创建新 session，total_secs 被重置为 0）。
    // 保持唯一 roomId 贯穿 generation-preview → classroom 全程。
    const stableRoomId = nanoid();
    const tempSessionState = {
      roomId: stableRoomId,
      requirements: null,
      pdfText: '',
      pdfImages: [],
      imageStorageIds: [],
      pdfStorageKey: undefined,
      pdfFileName: undefined,
      pdfFileMimeType: undefined,
      pdfProviderId: undefined,
      pdfProviderConfig: undefined,
      sceneOutlines: null,
      currentStep: 'generating' as const,
    };
    // 【2026-06-04 C+缓存】不再写 sessionStorage，临时 roomId 存到 timerRoomId
// 完整 sessionState 等"准备完毕"再调 save_generation_session API 存 DB

    // ========== 课时预检（点击"进入课堂"时调用）==========
    // 【2026-06-03 课时核心理则】见后端 canAffordOneMinute 顶部注释
    // 后端规则：负余额/三关全无 → 拒绝，引导「充值」或「购买套餐」
    // 此处是"软预检"：失败时弹 AlertDialog，但即使此接口异常（网络/解析错），
    // 也会继续走下方流程（实际 doEnter 会再硬卡一次，安全性有保障）。
    try {
      const ts = Math.floor(Date.now() / 1000);
      const nonce = Math.random().toString(36).substring(2) + ts.toString(36);
      const payload = '0' + ts + nonce + 'balance_chk' + 'balance_check';
      const key = 'dongniai_timer_sym_key_v2_mT8nQk3pRs5LzX9j';
      const enc = new TextEncoder();
      // 【2026-07-15 P0 修复】crypto.subtle 在 HTTP / 老浏览器 / WebView / 沙箱下可能 undefined。
      // 之前 await crypto.subtle.importKey(...) 直接抛 TypeError → catch 后 console.warn 看着像 bug。
      // 现在：若 crypto.subtle 不可用，整个软预检直接跳过（让后端 doEnter 硬卡），避免误导性 console 错误。
      if (!crypto?.subtle?.importKey || !crypto?.subtle?.sign) {
        console.warn('[balance_check] 当前环境不支持 crypto.subtle，跳过软预检（由后端 doEnter 硬卡）');
        throw new Error('crypto.subtle unavailable in this environment');
      }
      const cryptoKey = await crypto.subtle.importKey('raw', enc.encode(key), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
      const signature = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(payload));
      const sig = Array.from(new Uint8Array(signature)).map((b: number) => b.toString(16).padStart(2, '0')).join('');
      const formData = new URLSearchParams({ room_id: 'balance_chk', ts: String(ts), nonce, sig });
      const res = await fetch('/classroom_timer.php?a=balance_check', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      const json = await res.json();
      if (json.code === -1) {
        // 预检失败 → 弹 AlertDialog，弹窗同时显示「充值」和「购买套餐」两个按钮
        // 透传后端 reason 文案 + URLs（recharge_url / plan_url）
        // source='negative_balance' 时引导用户先补负数；source='balance' 时引导充值或买套餐
        setBalanceError({
          message: json.msg,
          source: json.data?.source ?? 'balance',
          balance: json.data?.balance ?? 0,
          rechargeUrl: json.data?.recharge_url ?? '/index/recharge/recharge.html',
          planUrl: json.data?.plan_url ?? '/dongniai/plan/index',
        });
        return;
      }
    } catch (err) {
      // 【P0-3 修复 2026-06-03】原来是 `catch { /* ignore */ }` 静默吞掉所有异常
      // 风险：网络故障/5xx/签名异常时 → 静默跳过 → 用户进 preview → enter() 才被 PHP 拒绝
      // 现在改为：记录到 console 便于排查，仍继续走下方流程（PHP doEnter 会再卡一次，安全无漏）
      // 用户体验略差（先看到 preview 页再被拒），但保证安全 + 留有排查线索
      console.warn('[balance_check] 软预检失败，将由后端硬卡:', err);
    }

    try {
      const userProfile = useUserProfileStore.getState();

      // 【2026-06-08 老板新需求】如果有上传图片(无论有没有填提示词)
      //   1. 调 /api/ocr 让后台配置的 OCR 模型识别图片内容
      //   2. 把 OCR 文本合并到 requirement(供后续 LLM 生成课堂提示词)
      //   3. 同时把图片 dataURL 作为 vision images 传给后续 scene-outlines-stream
      let effectiveRequirement = form.requirement.trim();
      let ocrProvider: string | undefined;
      let ocrModel: string | undefined;

      if (doneImages.length > 0) {
        setError(null);
        setOcrOverlay({ status: 'running', message: `正在识别 ${doneImages.length} 张图片内容...` });
        try {
          const ocrRes = await fetch('/dongniai/api/ocr/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId: stableRoomId,
              images: doneImages.map((img) => ({
                base64: img.dataUrl,
                mimeType: img.mimeType,
                fileName: img.fileName,
              })),
            }),
          });
          const ocrJson = await ocrRes.json();
          if (!ocrRes.ok || !ocrJson?.success) {
            setOcrOverlay(null);
            setError('OCR 识别失败：' + (ocrJson?.msg || `HTTP ${ocrRes.status}`));
            return;
          }
          // 【修复 2026-06-08】apiSuccess() spread data 到顶层,不在 data 嵌套
          const ocrText = (ocrJson.text as string)?.trim() || '';
          ocrProvider = ocrJson.provider;
          ocrModel = ocrJson.modelId;
          if (ocrText) {
            // 有提示词 → 在 requirement 后面追加图片识别内容
            // 没提示词 → 拿 OCR 文本直接当 requirement(LLM 会根据图片内容生成提示词)
            if (effectiveRequirement) {
              effectiveRequirement = `${effectiveRequirement}\n\n[以下是从 ${doneImages.length} 张上传图片中识别的内容，供你设计课堂参考]\n${ocrText}`;
            } else {
              effectiveRequirement = `用户上传了 ${doneImages.length} 张图片，识别内容如下。请基于这些内容设计一个能帮助用户答疑解惑、举一反三的互动课堂。\n\n[图片识别内容]\n${ocrText}`;
            }
          } else {
            // OCR 返回空(可能图片没文字) — 但 LLM 仍能从图片本身获取信息
            if (!effectiveRequirement) {
              effectiveRequirement = `用户上传了 ${doneImages.length} 张图片。请基于图片内容(作为视觉输入)设计一个能帮助用户答疑解惑、举一反三的互动课堂。`;
            }
          }
        } catch (e) {
          setOcrOverlay(null);
          setError('OCR 识别异常：' + (e instanceof Error ? e.message : String(e)));
          return;
        }
        setOcrOverlay(null);
      }

      const requirements: UserRequirements = {
        requirement: effectiveRequirement,
        // Always use '同学' as the student address
        userNickname: '同学',
        userBio: userProfile.bio || undefined,
        interactiveMode: form.interactiveMode,
      };

      let pdfStorageKey: string | undefined;
      let pdfFileName: string | undefined;
      // 【2026-07-28 决策点 2.3】保留原文件 mime, parse-pdf vendor 用 magic bytes 派发 mime dispatch
      let pdfFileMimeType: string | undefined;
      let pdfProviderId: string | undefined;
      let pdfProviderConfig: { apiKey?: string; baseUrl?: string } | undefined;

      if (form.pdfFile) {
        pdfStorageKey = await storePdfBlob(form.pdfFile);
        pdfFileName = form.pdfFile.name;
        pdfFileMimeType = form.pdfFile.type || undefined;

        const settings = useSettingsStore.getState();
        pdfProviderId = settings.pdfProviderId;
        const providerCfg = settings.pdfProvidersConfig?.[settings.pdfProviderId];
        if (providerCfg) {
          pdfProviderConfig = {
            apiKey: providerCfg.apiKey,
            baseUrl: providerCfg.baseUrl,
          };
        }
      } else if (form.courseMaterials.length > 0) {
        // 【2026-07-29 多文件上传 additive】pdfFile 未传但 courseMaterials 有, 兑底取第 1 个
        // 保留单文件 PDF provider 流程 (cast mime + apiKey + baseUrl)
        // 不破坏现有 commit 65d4a198 cast mime 逻辑, 仅覆盖 "pdfFile = null 但多文件上传有内容" 场景
        const firstMaterial = form.courseMaterials[0];
        pdfStorageKey = await storePdfBlob(firstMaterial.file);
        pdfFileName = firstMaterial.name;
        pdfFileMimeType = firstMaterial.type || undefined;

        const settings = useSettingsStore.getState();
        pdfProviderId = settings.pdfProviderId;
        const providerCfg = settings.pdfProvidersConfig?.[settings.pdfProviderId];
        if (providerCfg) {
          pdfProviderConfig = {
            apiKey: providerCfg.apiKey,
            baseUrl: providerCfg.baseUrl,
          };
        }
      }

      // 【2026-06-08】图片走 vision mode 路径：转成 PdfImage 格式喂给 LLM
      // 让 LLM 可以在生成课堂时“看”到图片(不止文字)
      type PdfImageLike = {
        id: string;
        src: string;
        pageNumber: number;
        description?: string;
      };
      const imagePdfImages: PdfImageLike[] = doneImages.map((img, idx) => ({
        id: `uploaded_img_${idx + 1}`,
        src: img.dataUrl || '',
        pageNumber: idx + 1,
        description: `用户上传的第 ${idx + 1} 张图片（${img.fileName}，${(img.size / 1024).toFixed(1)}KB，来源：${img.source === 'mobile' ? '手机扫码' : '本机'})`,
      }));
      const imageMapping: Record<string, string> = {};
      for (const img of imagePdfImages) {
        if (img.src) imageMapping[img.id] = img.src;
      }

      const sessionState = {
        roomId: stableRoomId, // 关键修复：复用同一个 roomId（之前这里又 nanoid() 一次）
        requirements,
        pdfText: '',
        pdfImages: imagePdfImages,
        imageStorageIds: [],
        imageMapping,
        pdfStorageKey,
        pdfFileName,
        pdfFileMimeType,
        pdfProviderId,
        pdfProviderConfig,
        // 2026-06-08 记录哪些图片走 OCR 路径、用了哪个 OCR 模型
        ocrProvider,
        ocrModel,
        ocrImageCount: doneImages.length,
        sceneOutlines: null,
        currentStep: 'generating' as const,
      };
      // 【2026-06-04 C+缓存】存到 DB + Redis（替代 sessionStorage）
      // session_id 用纯 nanoid（后端自动用当前 session 的 user_id 当 owner）
      const sessionId = nanoid();
      try {
        const saveRes = await fetch('/classroom_timer.php?a=save_generation_session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'session_id=' + encodeURIComponent(sessionId) + '&state=' + encodeURIComponent(JSON.stringify(sessionState)),
          credentials: 'include',
        }).then((r) => r.json());
        if (saveRes.code !== 1) {
          throw new Error(saveRes.msg || 'save_generation_session 失败');
        }
        console.log('[handleGenerate] session saved:', sessionId, 'state_size=', saveRes.data?.state_size);
      } catch (e) {
        log.error('Failed to save generation session:', e);
        setError('保存课堂配置失败：' + (e instanceof Error ? e.message : String(e)));

        return;  // 不跳，等用户重试
      }
      router.push('/generation-preview/?session_id=' + encodeURIComponent(sessionId));

      // Record latest classroom entry timestamp (used for 30-day popup logic)
      try {
        localStorage.setItem('plan_popup_last_classroom_time', String(Date.now()));
      } catch { /* ignore */ }
    } catch (err) {
      log.error('Error preparing generation:', err);
      setError(err instanceof Error ? err.message : t('upload.generateFailed'));
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('classroom.today');
    if (diffDays === 1) return t('classroom.yesterday');
    if (diffDays < 7) return `${diffDays} ${t('classroom.daysAgo')}`;
    return date.toLocaleDateString();
  };

  const canGenerate =
    !!form.requirement.trim() || form.uploadedImages.some((i) => i.status === 'done');

  // 2026-07-29 习题演练模式：触发与正常"进入课堂"相同流程，但走 mode=exercise 计费
  //   - 触发场景：用户点 page.tsx 的"习题演练"按钮 + AlertDialog 确认
  //   - 复用 handleGenerate 流程：建 session、生成场景、进入 classroom
  //   - 不同：传给 hook 的 isExercise=true → enter 写入 model_used="__exercise__" 前缀 → stop 时识别
  //   - 账单：固定 2 分钟 + chat_content="习题演练 | time range" → billinglog 备注显示"习题演练"
  // 2026-07-30 v2 老板需求：去"进入习题"按钮 → 删 pendingExerciseSessionId / handleExercise / exerciseDialogOpen / 二次确认弹窗（dead code）
  // 习题演练现在只通过 TopNav "习题演练" 按钮跳独立入口页 /dongniai/exercise/，不依赖主页任何 trigger
  const [exerciseConfigOpen, setExerciseConfigOpen] = useState(false);
  // 主输入框插入提示词后短暂高亮 1.8s 提示用户"已插入"
  const [insertHighlight, setInsertHighlight] = useState(false);
  const [exerciseConfig, setExerciseConfig] = useState({
    education_level: '',
    education_grade: '',
    subject: '',
    textbook_version: '',
    // 2026-07-30 v2 老板需求：去掉"演练类型"字段，改为"学生目前的基础水平"
    student_level: '一般',
    student_level_other: '', // 当选"其他"时的自由文本
    save_form: false,
  });
  const [savedForms, setSavedForms] = useState<Array<{ id: number; name: string; education_level: string; education_grade: string; subject: string; textbook_version: string }>>([]);

  // 2026-07-30 v5 老板需求：点击"年级/年龄"输入框时直接弹出所有联动选项（datalist 默认行为需要先输入文字才显示）
  const [pageGradeDropdownOpen, setPageGradeDropdownOpen] = useState(false);
  const pageGradeInputRef = useRef<HTMLInputElement | null>(null);

  // 2026-07-30 localStorage 持久化上次填写的配置，弹窗打开时自动加载（老板需求：弹窗里显示填写过的，方便直接插入 / 再次填写 / 选择已保存的）
  const LAST_EXERCISE_CONFIG_KEY = 'dongniai_last_exercise_config';

  // 【v6.97.60+31 P0 修复 2026-09-17 老板报】"学员详情"弹窗点开报错 'lastSavedAt is not defined' 根修
  //   - 修前: v6.97.60 注释写"已用 state + useEffect (异步), render 这里不再读 localStorage/Date", 但 useState 实际从未声明, 只有下面那个 useEffect 调 setLastSavedAt
  //     + 1695 行 / 1711 行 render 路径里直接读 lastSavedAt. 点"学员详情"→ exerciseConfigOpen=true → 重渲 → render 走到 1711 行 → ReferenceError: lastSavedAt is not defined
  //     → React error boundary 触发 "当前页面遇到问题 / lastSavedAt is not defined"
  //   - 修后: 补 useState 声明, 初始 '' (与注释承诺的"SSR 时 lastSavedAt='' / client hydration 时 lastSavedAt=''" 一致), useEffect mount 后异步从 localStorage 填充
  const [lastSavedAt, setLastSavedAt] = useState<string>('');

  // 【v6.97.60 P0 FIX 2026-08-03】Mac Safari / iPad Safari 'Application error' hydration mismatch 修复
  //   组件 mount 后异步从 localStorage 读 _savedAt 并格式化, 避免 render 路径里读 localStorage+Date 触发 hydration mismatch
  //   SSR 时 lastSavedAt='', client hydration 时 lastSavedAt='' (一致), mount 后 useEffect 异步更新
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const cached = localStorage.getItem(LAST_EXERCISE_CONFIG_KEY);
      if (!cached) return;
      const parsed = JSON.parse(cached);
      if (!parsed._savedAt) return;
      const d = new Date(parsed._savedAt);
      const now = new Date();
      const diffMin = Math.round((now.getTime() - d.getTime()) / 60000);
      let display = '';
      if (diffMin < 1) display = '刚刚';
      else if (diffMin < 60) display = `${diffMin} 分钟前`;
      else if (diffMin < 1440) display = `${Math.floor(diffMin / 60)} 小时前`;
      else display = `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      setLastSavedAt(display);
    } catch { /* ignore */ }
  }, []);

  // 打开配置弹窗：1) 自动加载上次填写 2) 拉取已保存表单
  const openExerciseConfig = async () => {
    setExerciseConfigOpen(true);
    // 1) 自动加载上次填写（localStorage 持久化，跨刷新/重启都还在）
    try {
      const cached = typeof window !== 'undefined' ? localStorage.getItem(LAST_EXERCISE_CONFIG_KEY) : null;
      if (cached) {
        const parsed = JSON.parse(cached);
        setExerciseConfig((prev) => ({
          ...prev,
          education_level: parsed.education_level || prev.education_level,
          education_grade: parsed.education_grade || prev.education_grade,
          subject: parsed.subject || prev.subject,
          textbook_version: parsed.textbook_version || prev.textbook_version,
          // 2026-07-30 v2 兼容旧 localStorage：老数据里有 exercise_type 但没有 student_level
          // 新数据只有 student_level / student_level_other，缺省回退 '一般'
          student_level: parsed.student_level || (parsed.exercise_type ? '一般' : prev.student_level),
          student_level_other: parsed.student_level_other || prev.student_level_other,
          save_form: false, // 自动加载时不默认勾选保存
        }));
      }
    } catch { /* ignore */ }
    // 2) 拉取已保存表单列表（供下方卡片网格选择）
    try {
      const res = await fetch('/dongniai/plan/listExerciseForms', { credentials: 'include' });
      const json = await res.json();
      if (json.code === 1 && Array.isArray(json.data?.forms)) setSavedForms(json.data.forms);
    } catch { /* ignore */ }
  };

  // 加载已保存表单到当前配置
  const loadSavedForm = (id: number) => {
    const f = savedForms.find((x) => x.id === id);
    if (!f) return;
    setExerciseConfig((c) => ({
      ...c,
      education_level: f.education_level,
      education_grade: f.education_grade,
      subject: f.subject,
      textbook_version: f.textbook_version,
    }));
  };

  // 重置配置为默认值（"重新填写"按钮）
  const resetExerciseConfig = () => {
    setExerciseConfig({
      education_level: '',
      education_grade: '',
      subject: '',
      textbook_version: '',
      // 2026-07-30 v2 老板需求：去掉"演练类型"，改为"学生目前的基础水平"
      student_level: '一般',
      student_level_other: '',
      save_form: false,
    });
    try { localStorage.removeItem(LAST_EXERCISE_CONFIG_KEY); } catch { /* ignore */ }
  };

  // 2026-07-30 v2 老板需求：去掉"演练类型"6 卡片，改为"学生目前的基础水平"6 选项 + "其他"
  const STUDENT_LEVEL_OPTIONS = ['零基础', '较差', '一般', '良好', '优秀', '尖子生'] as const;

  // 2026-07-30 v3 老板需求：阶段跟 dongniai/plan/myForms 完全一致（11 项），年级联动选项跟 myForms 一样
  //   - myForms.html 阶段：学前/小学/初中/高中/专科/本科/硕士/博士/博士后/成人/其他
  //   - GRADE_MAP：小学-博士 7 个有联动年级；学前/成人/其他 用年龄；博士后 跳过
  //   - 复用入口页 (/dongniai/exercise/) 的字段模式（input + datalist 兼顾联动+自由填）
  const GRADE_MAP: Record<string, string[]> = {
    '小学': ['一年级', '二年级', '三年级', '四年级', '五年级', '六年级'],
    '初中': ['初一', '初二', '初三'],
    '高中': ['高一', '高二', '高三'],
    '专科': ['大一', '大二', '大三'],
    '本科': ['大一', '大二', '大三', '大四'],
    '硕士': ['研一', '研二', '研三'],
    '博士': ['博一', '博二', '博三', '博四', '博五'],
  };

  // 拼装 prompt — 学生基础水平优先（用于演练/课堂 prompt 上下文）
  const buildExercisePrompt = (cfg: typeof exerciseConfig): string => {
    const parts: string[] = [];
    // 2026-07-30 v2 老板需求：去【演练类型】+【要求】改成【学生基础水平】
    const levelLabel = cfg.student_level === '其他' ? (cfg.student_level_other || '其他') : cfg.student_level;
    if (levelLabel) parts.push(`【学生基础水平】${levelLabel}`);
    if (cfg.education_level) parts.push(`【阶段】${cfg.education_level}`);
    if (cfg.education_grade) parts.push(`【年级】${cfg.education_grade}`);
    if (cfg.subject) parts.push(`【科目】${cfg.subject}`);
    if (cfg.textbook_version) parts.push(`【教材版本】${cfg.textbook_version}`);
    return parts.join('\n');
  };

  // 提交配置："插入提示词" → 关弹窗 → 写入主输入框（可再编辑）+ 主输入框紫色高亮脉冲 1.8s
  //   - 不写 sessionStorage、不弹二次确认（这些移到"进入习题"按钮处理）
  //   - 主输入框如有内容则追加（不覆盖老板已写好的内容）
  //   - 同时持久化到 localStorage，下次打开弹窗自动加载（弹窗里显示填写过的）
  const submitExerciseConfig = async () => {
    // 2026-07-30 v4 老板需求：年级/年龄也必填（与科目、阶段一致）
    if (!exerciseConfig.subject || !exerciseConfig.education_level || !exerciseConfig.education_grade.trim()) {
      toast.error('请填写：阶段 + 年级/年龄 + 科目');
      return;
    }
    // 持久化本次填写 → 下次打开弹窗自动加载（localStorage 不依赖登录态）
    try {
      localStorage.setItem(LAST_EXERCISE_CONFIG_KEY, JSON.stringify({
        education_level: exerciseConfig.education_level,
        education_grade: exerciseConfig.education_grade,
        subject: exerciseConfig.subject,
        textbook_version: exerciseConfig.textbook_version,
        // 2026-07-30 v2 老板需求：去演练类型，改存"学生基础水平"
        student_level: exerciseConfig.student_level,
        student_level_other: exerciseConfig.student_level_other,
        _savedAt: new Date().toISOString(),
      }));
    } catch { /* ignore */ }
    const cfgPrompt = buildExercisePrompt(exerciseConfig);
    setExerciseConfigOpen(false);
    setForm((prev) => {
      const existing = prev.requirement.trim();
      const next = existing ? `${existing}\n\n${cfgPrompt}` : cfgPrompt;
      updateRequirementCache(next);
      return { ...prev, requirement: next };
    });
    setInsertHighlight(true);
    setTimeout(() => setInsertHighlight(false), 1800);
    if (exerciseConfig.save_form) {
      try {
        const fd = new FormData();
        fd.append('education_level', exerciseConfig.education_level);
        fd.append('education_grade', exerciseConfig.education_grade);
        fd.append('subject', exerciseConfig.subject);
        fd.append('textbook_version', exerciseConfig.textbook_version);
        await fetch('/dongniai/plan/saveExerciseForm', {
          method: 'POST',
          body: fd,
          credentials: 'include',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
      } catch { /* ignore */ }
    }
  };

  // 2026-07-30 v2 老板需求：去"进入习题"按钮，triggerEnterExercise / handleExercise / pendingExerciseSessionId / exerciseDialogOpen / 二次确认弹窗 全部废弃

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (canGenerate) handleGenerate();
    }
  };

  return (
    <>
      {/* 站点顶部导航条（仿 ceshi.xiaoquanwk.com 顶部的 5 个按钮）—— 仅 dongniai 首页显示 */}
      <TopNav />
      <div className="min-h-[100dvh] w-full bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 flex flex-col items-center p-4 pt-16 md:p-8 md:pt-16 overflow-x-hidden">
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          onChange={handleFileChange}
          className="hidden"
        />
      {/* ═══ Top-right pill (unchanged) ═══ */}
      <div
        ref={toolbarRef}
        className="fixed top-4 right-4 z-50 flex items-center gap-1 bg-white/60 dark:bg-gray-800/60 backdrop-blur-md px-2 py-1.5 rounded-full border border-gray-100/50 dark:border-gray-700/50 shadow-sm"
      >
        {/* Language Selector */}
        <LanguageSwitcher onOpen={() => setThemeOpen(false)} />

        <div className="w-[1px] h-4 bg-gray-200 dark:bg-gray-700" />

        {/* Theme Selector */}
        <div className="relative">
          <button
            onClick={() => {
              setThemeOpen(!themeOpen);
            }}
            className="p-2 rounded-full text-gray-400 dark:text-gray-500 hover:bg-white dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200 hover:shadow-sm transition-all"
          >
            {theme === 'light' && <Sun className="w-4 h-4" />}
            {theme === 'dark' && <Moon className="w-4 h-4" />}
            {theme === 'system' && <Monitor className="w-4 h-4" />}
          </button>
          {themeOpen && (
            <div className="absolute top-full mt-2 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden z-50 min-w-[140px]">
              <button
                onClick={() => {
                  setTheme('light');
                  setThemeOpen(false);
                }}
                className={cn(
                  'w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2',
                  theme === 'light' &&
                    'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
                )}
              >
                <Sun className="w-4 h-4" />
                {t('settings.themeOptions.light')}
              </button>
              <button
                onClick={() => {
                  setTheme('dark');
                  setThemeOpen(false);
                }}
                className={cn(
                  'w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2',
                  theme === 'dark' &&
                    'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
                )}
              >
                <Moon className="w-4 h-4" />
                {t('settings.themeOptions.dark')}
              </button>
              <button
                onClick={() => {
                  setTheme('system');
                  setThemeOpen(false);
                }}
                className={cn(
                  'w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2',
                  theme === 'system' &&
                    'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
                )}
              >
                <Monitor className="w-4 h-4" />
                {t('settings.themeOptions.system')}
              </button>
            </div>
          )}
        </div>

        <div className="w-[1px] h-4 bg-gray-200 dark:bg-gray-700" />



        {/* Auth: show nickname+logout if logged in, else login+register */}
        {(() => {
          const { loggedIn, nickname } = useAuthStore();
          if (loggedIn) {
            return (
              <>
                <NoticeBell />
                {/* 【v6.97.85+11 P0 修复 2026-09-02 老板报】同 v6.97.83+5 模板, 用户中心/退出 链接 href 改绝对路径
                    防 SSR 渲染相对路径 + 防右键复制 + 防 Nginx @fastadmin 兜底重写 → /dashboard?ref=addtabs */}
                <a
                  href={typeof window !== 'undefined'
                    ? window.location.origin + '/index/user/index.html'
                    : '/index/user/index.html'}
                  className="px-3 py-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-full transition-all whitespace-nowrap"
                >
                  👤{nickname}
                </a>
                <a
                  href={typeof window !== 'undefined'
                    ? window.location.origin + '/index/user/logout.html?url=/dongniai/'
                    : '/index/user/logout.html?url=/dongniai/'}
                  className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 rounded-full transition-all"
                >
                  退出
                </a>
              </>
            );
          }
          return (
            <>
              {/* 【v6.97.83+5 P0 修复 2026-09-01 老板报】a 标签 href 改用绝对路径, 绕开 basePath 重写 */}
              <a
                href={typeof window !== 'undefined'
                  ? window.location.origin + '/index/user/login.html?url=/dongniai/'
                  : '/index/user/login.html?url=/dongniai/'}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-full transition-all"
              >
                登录
              </a>
              {/* 【v6.97.83+5 P0 修复 2026-09-01 老板报】同 login, 注册链接也升级绝对路径 */}
              <a
                href={typeof window !== 'undefined'
                  ? window.location.origin + '/index/user/register.html?url=/dongniai/'
                  : '/index/user/register.html?url=/dongniai/'}
                className="px-3 py-1.5 text-xs font-medium bg-purple-500 text-white rounded-full hover:bg-purple-600 transition-all"
              >
                注册
              </a>
            </>
          );
        })()}
      </div>
          {/* 课时不足/余额不足弹窗（点击"进入课堂"失败时弹出）
              【2026-06-03 课时核心理则】根据后端 source 字段区分文案：
                - 'negative_balance'（余额已透支）：主推"前往充值"补齐负数
                - 'balance'（套餐/赠送/余额全无）：同时推"购买套餐"+"前往充值"
                - 其他：通用文案
              两个跳转按钮可同时展示，引导用户根据自己情况选择。 */}
          {/* 2026-07-29 习题演练配置弹窗（前置：填基本情况+演练类型） */}
          <AlertDialog open={exerciseConfigOpen} onOpenChange={setExerciseConfigOpen}>
            <AlertDialogContent className="max-w-2xl w-[calc(100vw-2rem)] p-0 overflow-hidden gap-0 border-0 shadow-2xl">
              <div className="relative bg-gradient-to-br from-indigo-500 via-violet-500 to-purple-600 px-6 py-5 text-white">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">📝</div>
                  <div>
                    {/* 2026-07-30 v2 老板需求：弹窗标题从"配置习题演练"改名为"学员详情"，常亮入口让用户随时查看/编辑学员情况 */}
                    <h2 className="text-lg font-semibold leading-tight">学员详情</h2>
                    <p className="text-xs text-white/80 mt-0.5">填写学员的基本情况，老师会按情况生成专属内容</p>
                  </div>
                </div>
                <button onClick={() => setExerciseConfigOpen(false)} className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 rounded-md w-7 h-7 flex items-center justify-center transition-colors" aria-label="关闭">×</button>
              </div>
              <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
                <AlertDialogHeader className="sr-only">
                  <AlertDialogTitle>学员详情</AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogDescription className="space-y-5">
                    {/* 2026-07-30 已填写过的内容 banner：自动加载上次填的，展示摘要 + 重新填写按钮（老板需求：弹窗里显示填写过的） */}
                    {(() => {
                      const hasContent = exerciseConfig.education_level || exerciseConfig.subject || exerciseConfig.education_grade || exerciseConfig.textbook_version;
                      if (!hasContent) return null;
                      // 【v6.97.60】lastSavedAt 已用 state + useEffect (异步), render 这里不再读 localStorage/Date
                      const summary = [
                        exerciseConfig.education_level,
                        exerciseConfig.education_grade,
                        exerciseConfig.subject,
                        exerciseConfig.textbook_version,
                      ].filter(Boolean).join(' · ');
                      // 2026-07-30 v2 老板需求：去演练类型，改为显示"学生基础水平"
                      const levelDisplay = exerciseConfig.student_level === '其他'
                        ? (exerciseConfig.student_level_other || '其他')
                        : exerciseConfig.student_level;
                      return (
                        <div className="rounded-lg bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 px-3.5 py-2.5 flex items-center justify-between gap-3">
                          <div className="text-xs leading-relaxed min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 font-medium text-violet-700">
                              <span>📌</span>
                              <span>已填写过的内容{lastSavedAt && `（${lastSavedAt}）`}</span>
                              <span className="text-[10px] font-normal text-violet-500/80">· 可直接编辑</span>
                            </div>
                            <div className="mt-1 text-[11px] text-slate-600 truncate">
                              {summary || '（部分字段为空）'}
                              {levelDisplay && <span className="ml-1 text-violet-600">· 基础水平：{levelDisplay}</span>}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={resetExerciseConfig}
                            className="text-[11px] text-slate-500 hover:text-rose-600 hover:bg-rose-50 px-2.5 py-1 rounded transition-colors shrink-0 flex items-center gap-1"
                            title="清空所有字段，重新填写"
                          >
                            <span>🔄</span><span>重新填写</span>
                          </button>
                        </div>
                      );
                    })()}
                    {savedForms.length > 0 && (
                      <div>
                        <div className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1.5">
                          <span>📋</span><span>从已保存的基本情况选择</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {savedForms.map((f) => {
                            const isSelected = exerciseConfig.education_level === f.education_level && exerciseConfig.subject === f.subject && exerciseConfig.textbook_version === f.textbook_version;
                            return (
                              <button key={f.id} type="button" onClick={() => loadSavedForm(f.id)}
                                className={`text-left px-3 py-2.5 rounded-lg border transition-all ${isSelected ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-200 shadow-sm' : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 bg-white'}`}>
                                <div className="text-xs font-medium text-slate-800">{f.name || `${f.education_level || '未设'} · ${f.subject || '未设科目'}`}</div>
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {f.education_level && <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{f.education_level}</span>}
                                  {f.subject && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">{f.subject}</span>}
                                  {f.textbook_version && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">{f.textbook_version}</span>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs font-medium text-slate-700 mb-2 flex items-center gap-1.5">
                        <span>🎓</span><span>学习基本情况</span><span className="text-rose-500">*</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                      <div>
                      <label className="text-xs text-slate-500">阶段 *</label>
                      {/* 2026-07-30 v3 老板需求：跟 dongniai/plan/myForms 完全一致（11 项阶段） */}
                      <select
                        className="mt-1 w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white"
                        value={exerciseConfig.education_level}
                        onChange={(e) => {
                          const lv = e.target.value;
                          setExerciseConfig((c) => {
                            // 阶段切换 → 旧年级如果不在新阶段 GRADE_MAP 里则清掉（避免跨阶段不匹配）
                            if (c.education_grade && GRADE_MAP[lv] && !GRADE_MAP[lv].includes(c.education_grade)) {
                              return { ...c, education_level: lv, education_grade: '' };
                            }
                            return { ...c, education_level: lv };
                          });
                        }}
                      >
                        <option value="">— 阶段 —</option>
                        {['学前','小学','初中','高中','专科','本科','硕士','博士','博士后','成人','其他'].map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                    <div>
                      {/* 2026-07-30 v3 老板需求：'年级' → '年级/年龄'，选项跟 myForms 一样（datalist 联动 + 自由填）；必填项（红星）；永远可编辑（去掉 disabled，让用户填完可直接修改）
                         2026-07-30 v5 老板需求：点击输入框即弹所有联动选项（datalist 默认需要先输入才显示）
                           - onFocus 打开自定义下拉浮层（点空白处关闭）
                           - onMouseDown preventDefault 让 dropdown 项点击先于 blur 触发
                           - 保留 datalist：边输边筛
                           - 无 GRADE_MAP 的阶段不弹下拉（只有 placeholder） */}
                      <label className="text-xs text-slate-500">年级/年龄 <span className="text-rose-500">*</span></label>
                      <div className="relative">
                        <input
                          ref={pageGradeInputRef}
                          list="page-grade-suggestions"
                          type="text"
                          placeholder={
                            GRADE_MAP[exerciseConfig.education_level]?.length
                              ? '如：六年级（可自由填写）'
                              : exerciseConfig.education_level === '学前'
                              ? '如：3岁 / 学前班'
                              : exerciseConfig.education_level === '博士后'
                              ? '如：研究方向 / 自由填写'
                              : '如：30岁 / 自由填写'
                          }
                          className="mt-1 w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white"
                          value={exerciseConfig.education_grade}
                          onChange={(e) => setExerciseConfig((c) => ({ ...c, education_grade: e.target.value }))}
                          onFocus={() => {
                            // 只在有联动选项时才打开下拉（学前/博士后/成人/其他不弹）
                            if (GRADE_MAP[exerciseConfig.education_level]?.length) setPageGradeDropdownOpen(true);
                          }}
                          onBlur={() => {
                            // setTimeout 让 mousedown 先触发 dropdown 项选择
                            setTimeout(() => setPageGradeDropdownOpen(false), 150);
                          }}
                          maxLength={20}
                        />
                        {/* 自定义下拉浮层 — 点击输入框即弹所有联动选项 */}
                        {pageGradeDropdownOpen && (GRADE_MAP[exerciseConfig.education_level] || []).length > 0 && (
                          <div
                            className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                            onMouseDown={(e) => {
                              // 防止 input blur 抢先触发导致 dropdown 项点击失效
                              e.preventDefault();
                            }}
                          >
                            {(GRADE_MAP[exerciseConfig.education_level] || []).map((v) => (
                              <button
                                key={v}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setExerciseConfig((c) => ({ ...c, education_grade: v }));
                                  setPageGradeDropdownOpen(false);
                                  pageGradeInputRef.current?.focus();
                                }}
                                className={cn(
                                  'w-full text-left px-3 py-2 text-xs transition-colors',
                                  'hover:bg-indigo-50 hover:text-indigo-700',
                                  exerciseConfig.education_grade === v && 'bg-indigo-50 text-indigo-700 font-medium'
                                )}
                              >
                                {v}
                              </button>
                            ))}
                            <div className="px-3 py-1.5 text-[10px] text-slate-400 border-t border-slate-100">
                              💡 也可直接输入自由文本（如"3 岁"、"成人高考"等）
                            </div>
                          </div>
                        )}
                      </div>
                      <datalist id="page-grade-suggestions">
                        {(GRADE_MAP[exerciseConfig.education_level] || []).map((v) => (
                          <option key={v} value={v} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      {/* 2026-07-30 v3 老板需求：'科目' 改填写（不是选择）— 灵活支持任何科目 */}
                      <label className="text-xs text-slate-500">科目 *</label>
                      <input
                        type="text"
                        placeholder="如：数学 / 编程 / 英语..."
                        className="mt-1 w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white"
                        value={exerciseConfig.subject}
                        onChange={(e) => setExerciseConfig((c) => ({ ...c, subject: e.target.value }))}
                        maxLength={50}
                      />
                    </div>
                    <div>
                      {/* 2026-07-30 v3 老板需求：'教材版本' 改填写（不是选择）— 灵活支持任何版本 */}
                      <label className="text-xs text-slate-500">教材版本</label>
                      <input
                        type="text"
                        placeholder="如：人教版 / 北师大版..."
                        className="mt-1 w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white"
                        value={exerciseConfig.textbook_version}
                        onChange={(e) => setExerciseConfig((c) => ({ ...c, textbook_version: e.target.value }))}
                        maxLength={50}
                      />
                    </div>
                  </div>
                </div>
                  {/* 2026-07-30 v2 老板需求：去"📚演练类型*" 6 卡片，改为"学生目前的基础水平是？" 6 选项 + 其他输入 */}
                  <div>
                    <div className="text-xs font-medium text-slate-700 mb-2 flex items-center gap-1.5">
                      <span>📊</span><span>学生目前的基础水平是？</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {STUDENT_LEVEL_OPTIONS.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setExerciseConfig((c) => ({ ...c, student_level: v, student_level_other: '' }))}
                          className={`text-xs px-3 py-2 rounded-lg border transition-all ${exerciseConfig.student_level === v ? 'border-violet-500 bg-violet-50 text-violet-700 font-medium ring-2 ring-violet-200' : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 bg-white text-slate-700'}`}
                        >
                          {v}
                        </button>
                      ))}
                      <button
                        key="其他"
                        type="button"
                        onClick={() => setExerciseConfig((c) => ({ ...c, student_level: '其他' }))}
                        className={`text-xs px-3 py-2 rounded-lg border transition-all ${exerciseConfig.student_level === '其他' ? 'border-violet-500 bg-violet-50 text-violet-700 font-medium ring-2 ring-violet-200' : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 bg-white text-slate-700'}`}
                      >
                        其他
                      </button>
                    </div>
                    {exerciseConfig.student_level === '其他' && (
                      <input
                        type="text"
                        placeholder="请描述学员的具体情况…"
                        value={exerciseConfig.student_level_other}
                        onChange={(e) => setExerciseConfig((c) => ({ ...c, student_level_other: e.target.value }))}
                        className="mt-2 w-full h-9 px-3 text-xs border border-violet-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-violet-200"
                        maxLength={50}
                      />
                    )}
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none hover:text-slate-800 transition-colors">
                    <input
                      type="checkbox"
                      checked={exerciseConfig.save_form}
                      onChange={(e) => setExerciseConfig((c) => ({ ...c, save_form: e.target.checked }))}
                      className="rounded border-slate-300"
                    />
                    <span>💾 保存为我的基本情况（以后可复用）</span>
                  </label>
                </AlertDialogDescription>
              </div>
              <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <button
                  onClick={() => setExerciseConfigOpen(false)}
                  className="text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 px-4 py-2 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={submitExerciseConfig}
                  className="bg-gradient-to-r from-indigo-500 via-violet-500 to-purple-600 hover:opacity-90 text-white text-sm font-medium px-6 py-2 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                >
                  <span>插入提示词</span>
                  <span>→</span>
                </button>
              </div>
            </AlertDialogContent>
          </AlertDialog>

          {/* 2026-07-30 v2 老板需求：进入习题确认弹窗（已废弃 dead code，已删 triggerEnterExercise / handleExercise） */}

          <AlertDialog open={!!balanceError} onOpenChange={(open) => { if (!open) setBalanceError(null); }}>
            <AlertDialogContent size="sm">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {balanceError?.source === 'negative_balance' ? '账户余额已透支' : '无法进入课堂'}
                </AlertDialogTitle>
                <AlertDialogDescription>{balanceError?.message}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
                {/* 购买套餐：source=balance（课时全无）或 source=negative_balance 时都显示 */}
                {balanceError && (balanceError.source === 'balance' || balanceError.source === 'negative_balance') && (
                  <AlertDialogAction
                    onClick={() => {
                      setBalanceError(null);
                      // 2026-06-04: 附加 return_url，支付完成跳回当前页
                      window.location.href = buildReturnUrl(balanceError.planUrl);
                    }}
                    className="bg-gradient-to-r from-blue-600 to-violet-600 hover:opacity-90 w-full"
                  >
                    购买套餐
                  </AlertDialogAction>
                )}
                {/* 前往充值：所有失败场景都显示 */}
                {balanceError && (
                  <AlertDialogAction
                    onClick={() => {
                      setBalanceError(null);
                      // 2026-06-04: 附加 return_url，支付完成跳回当前页
                      window.location.href = buildReturnUrl(balanceError.rechargeUrl);
                    }}
                    className="w-full"
                  >
                    前往充值
                  </AlertDialogAction>
                )}
                <AlertDialogCancel onClick={() => setBalanceError(null)} className="w-full mt-0">
                  取消
                </AlertDialogCancel>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

      {/* Course planning popup — shown on first textarea focus */}
      <Dialog open={planPopupOpen} onOpenChange={(open) => {
        if (!open) {
          // Record button position BEFORE closing so the shrink animation targets correctly
          const btn = document.querySelector('[data-plan-btn]') as HTMLElement | null;
          setPlanBtnRect(btn ? btn.getBoundingClientRect() : null);
          setPlanPopupClosing(true);
          // Wait for shrink animation to finish (300ms) before fully unmounting
          setTimeout(() => {
            setPlanPopupOpen(false);
            setPlanPopupClosing(false);
            if (planPopupPermanentDismiss) {
              localStorage.setItem('plan_popup_dismissed_forever', '1');
              setPlanPopupPermanentDismiss(false);
            }
          }, 320);
        }
      }}>
        <DialogContent
          showCloseButton={false}
          hideOverlay
          onEscapeKeyDown={(e) => { e.preventDefault(); }}
          onCloseAutoFocus={(e) => { e.preventDefault(); }}
          onPointerDownOutside={(e) => {
            // Allow outside clicks to pass through to the page
            // (so user can click the textarea without dismissing the popup)
            e.preventDefault();
          }}
          onInteractOutside={(e) => { e.preventDefault(); }}
          className="border-0 p-0 overflow-visible top-auto bottom-6 left-auto right-6 translate-x-0 translate-y-0 max-w-sm w-[320px] shadow-2xl shadow-blue-500/10"
        >
          {/* 【2026-06-12 修复 Radix UI 可访问性警告】DialogContent 必须有 DialogTitle，
              否则 screen reader 读不到弹窗内容。Plan popup 用 motion.div 自定义 UI，
              所以用 sr-only 包装一个语义化标题（替代 VisuallyHidden，0 依赖，更稳）。*/}
          <span className="sr-only">
            <DialogTitle>需要 AI 帮您规划课程吗？</DialogTitle>
          </span>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={
              planPopupClosing && planBtnRect
                ? {
                    opacity: 0,
                    scale: 0.1,
                    x: planBtnRect.left + planBtnRect.width / 2 - (typeof window !== 'undefined' ? window.innerWidth / 2 : 0),
                    y: planBtnRect.top + planBtnRect.height / 2 - (typeof window !== 'undefined' ? window.innerHeight / 2 : 0),
                    transition: { duration: 0.3, ease: [0.4, 0, 1, 1] },
                  }
                : { opacity: 0, scale: 0.95, y: -4, transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] } }
            }
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-blue-50/60 to-indigo-50/40 backdrop-blur-xl border border-blue-100/60 shadow-2xl shadow-blue-500/10 p-8 text-center"
          >
            {/* X 关闭按钮 - 放在motion.div内部，随弹窗一起收缩 */}
            <button
              onClick={() => {
                // 保存"30天不弹窗"到服务端（跨设备）
                fetch('/dongniai_public.php?method=planPopupPreference', {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ dismissed_30days: 1 }),
                }).catch(() => {});
                // 本地也记录（兼容旧逻辑）
                setSessionDismissed(true);
                try { sessionStorage.setItem('plan_popup_session_dismissed', 'true'); } catch {}
                localStorage.setItem('plan_popup_dismissed_30days', String(Date.now()));
                // 关闭弹窗，Dialog的onOpenChange会处理收缩动画
                setPlanPopupOpen(false);
              }}
              className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-slate-100/80 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-all duration-150 shadow-sm z-10"
              aria-label="关闭"
            >
              <X className="size-4" />
            </button>
            {/* Decorative top glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-16 bg-gradient-to-b from-blue-400/20 to-transparent rounded-b-full pointer-events-none" />

            {/* Animated icon */}
            <motion.div
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 shadow-lg shadow-blue-500/30"
            >
              <Sparkles className="h-8 w-8 text-white" />
            </motion.div>

            {/* Title */}
            <h2 className="mb-2 text-xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 bg-clip-text text-transparent">
                需要 AI 帮您规划课程吗？
              </span>
            </h2>

            {/* Subtitle */}
            <p className="mb-8 text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">
              智能课程规划，让您的学习更有条理、更高效
            </p>

            {/* Primary buttons row */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
              <button
                onClick={() => {
                  // 保存"选择了AI规划"到服务端（跨设备）
                  fetch('/dongniai_public.php?method=planPopupPreference', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ chose_to_plan: 1 }),
                  }).catch(() => {});
                  // 本地也记录
                  localStorage.setItem('plan_popup_dismissed_forever', '1');
                  setPlanPopupOpen(false);
                  // 跳转到AI规划课程页面
                  window.location.href = '/dongniai/plan/myForms';
                }}
                className="group relative flex-1 cursor-pointer rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 py-3 px-6 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all duration-200 hover:shadow-xl hover:shadow-blue-500/40 hover:-translate-y-0.5 active:translate-y-0 overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <Sparkles className="h-4 w-4 transition-transform group-hover:rotate-12" />
                  需要
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </button>
              {/*
                "不需要" 按钮:
                - 业务语义: 用户说"不需要"= 永久不再弹（这功能对我没用）
                - 跟 X 关闭(30天抑制) 不同，跟 "不再提醒" 效果相同但多一种表达入口
                - 修复: 之前错配为 30天抑制，导致 30天后又弹，用户感觉"还在弹"
                - 实现: 复用 no_remind=1 路径（与下方 "不再提醒" 按钮同源）
              */}
              <button
                onClick={() => {
                  fetch('/dongniai_public.php?method=planPopupPreference', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ no_remind: 1 }),
                  }).catch(() => {});
                  localStorage.setItem('plan_popup_dismissed_forever', '1');
                  setPlanPopupPermanentDismiss(false);
                  // 触发收缩动画（与"不再提醒"按钮复用同逻辑）
                  const btn = document.querySelector('[data-plan-btn]') as HTMLElement | null;
                  setPlanBtnRect(btn ? btn.getBoundingClientRect() : null);
                  setPlanPopupClosing(true);
                  setTimeout(() => {
                    setPlanPopupOpen(false);
                    setPlanPopupClosing(false);
                  }, 320);
                }}
                className="group relative flex-1 cursor-pointer rounded-xl border border-slate-200 bg-white/80 py-3 px-6 text-sm font-medium text-slate-600 shadow-sm transition-all duration-200 hover:border-blue-200 hover:bg-white hover:-translate-y-0.5 hover:shadow-md active:translate-y-0"
              >
                不需要
              </button>
            </div>

            {/* Divider + dismiss */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
              <button
                onClick={() => {
                  // 保存"不再提醒"到服务端（跨设备）
                  fetch('/dongniai_public.php?method=planPopupPreference', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ no_remind: 1 }),
                  }).catch(() => {});
                  // 本地也记录（兼容旧逻辑）
                  localStorage.setItem('plan_popup_dismissed_forever', '1');
                  setPlanPopupPermanentDismiss(false);
                  // 触发收缩动画
                  const btn = document.querySelector('[data-plan-btn]') as HTMLElement | null;
                  setPlanBtnRect(btn ? btn.getBoundingClientRect() : null);
                  setPlanPopupClosing(true);
                  setTimeout(() => {
                    setPlanPopupOpen(false);
                    setPlanPopupClosing(false);
                  }, 320);
                }}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors duration-150 tracking-wide"
              >
                不再提醒
              </button>
            </div>
          </motion.div>
        </DialogContent>
      </Dialog>

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={(open) => {
          setSettingsOpen(open);
          if (!open) setSettingsSection(undefined);
        }}
        initialSection={settingsSection}
      />

      {/* ═══ Background Decor ═══ */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '4s' }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: '6s' }}
        />
      </div>

      {/* ═══ Hero section: title + input ═══
          【2026-07-10 修复-对齐 OpenMAIC 官方 demo】原条件渲染：
            - 条件 classrooms.length === 0 ? 'justify-center min-h-[calc(100dvh-8rem)]' : 'mt-[10vh]'
          - 空状态（未登录）走 justify-center 分支时，hero box 505px 高 + TopNav 68px + pt-16 64px = 132px 顶部
            → hero 从 y=133 撑到 y=638（视口 633 → 底超 5px，页面无呼吸空间）
          - OpenMAIC 官方 demo (open.maic.chat) 实际渲染走 mt-[10vh] 分支 (logo y=192 + 下方 55px 呼吸)
          - 老板反馈 /dongniai/ 未登录 "页面下移"，参考 OpenMAIC 仓库统一改 mt-[10vh]
            → hero 自适应内容高度 (≈414px)，logo y=196，下方留 22px 视口呼吸空间
          - 条件分支因此等价，干脆去掉（两个分支原本意图都是 "顶部 + 呼吸"）*/}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={cn(
          'relative z-20 w-full max-w-[800px] flex flex-col items-center',
          // 【v6.97.62+2 P0 修复 2026-08-03 老板报 Mac/iPad Safari 1440 viewport 显示问题】
          //   真因: fixed loginCard 用 layout viewport (1425) 算 right 锚点,
          //   而 mainCard 用 visual viewport (1440) 居中. scrollbar gutter 15px
          //   偏差导致 mainCard.right=1113 跟 loginCard.x=1105 重叠 8px.
          //   Windows Chrome 抗锯齿模糊掉, Mac Safari 像素精确所以老板明显看到.
          //   修法: xl viewport (1280-1535) mainCard 收窄到 680px 留 60px gap,
          //   2xl (≥1536) 恢复 800px. loginCard 仍然 lg:block.
          'xl:max-w-[680px]',
          // 【v6.97.62+2 P0 修复 2026-08-03 老板报 Mac Safari 1440 viewport 显示问题】
          //   用 arbitrary breakpoint min-[1536px] 替代 2xl:max-w-[800px]
          //   真因: Tailwind v4 + globals.css @theme inline 组合下, 96rem
          //   breakpoint 块完全空 (0 个 2xl class 生成), 但 lg (64rem) +
          //   xl (80rem) 都正常. min-[1536px] 走 arbitrary breakpoint 路径
          //   强制生成, 不依赖 2xl theme token
          'min-[1536px]:max-w-[800px]',
          'mt-[10vh]',
        )}
      >
        {/* ── Logo ── */}
        <motion.img
          src="/logo-horizontal.webp"
          alt="dongniai"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            delay: 0.1,
            type: 'spring',
            stiffness: 200,
            damping: 20,
          }}
          className="h-12 md:h-16 mb-2 -ml-2 md:-ml-3"
        />

        {/* ── Slogan ── */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="text-sm text-muted-foreground/60 mb-8"
        >
          {t('home.slogan')}
        </motion.p>

        {/* ── Unified input area ── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35 }}
          className="w-full"
        >
          <div className="w-full rounded-2xl border border-border/60 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl shadow-xl shadow-black/[0.03] dark:shadow-black/20 transition-shadow focus-within:shadow-2xl focus-within:shadow-violet-500/[0.06]">
            {/* ── Greeting + Profile + Agents ── */}
            <div className="relative z-20 flex items-start justify-between">
              <GreetingBar />
              <div className="pr-3 pt-3.5 shrink-0">
                <AgentBar />
              </div>
            </div>

            {/* Textarea */}
            <textarea
              ref={textareaRef}
              placeholder={t('upload.requirementPlaceholder')}
              className={cn(
                'w-full resize-none border-0 bg-transparent px-4 pt-1 pb-2 text-[13px] leading-relaxed placeholder:text-muted-foreground/40 focus:outline-none min-h-[140px] max-h-[300px] transition-all duration-500',
                insertHighlight && 'ring-2 ring-violet-400 ring-offset-2 rounded-lg bg-violet-50/30'
              )}
              value={form.requirement}
              onChange={(e) => updateForm('requirement', e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                // [2026-06-25 修复 P0-UX] textarea onFocus 也禁用弹窗
                // 真因: 老板报告"输入提示词进入课堂 → 弹窗覆盖 → 流程走不通"
                //       → 用户点输入框是正常操作,不应该被弹窗覆盖
                //       → 弹窗只在用户主动点 "AI规划课程" 按钮时显示
                // 修法: onFocus 空函数,完全禁用自动弹窗
                //       AI规划按钮(line 1090+)仍可手动触发弹窗
              }}
              rows={4}
            />

            {/* Toolbar row */}
            <div className="px-3 pb-3 flex items-end gap-2">
              {/* 【2026-06-08 老板新需求 v2】"上传图片"按钮在"上传文件"左侧
                  【2026-07-31 v3 老板需求】手机版去掉"上传图片"按钮（mobile 用户主流程是文本/语音，不拍照上传）
                  - hidden md:flex → 默认 hidden, ≥768px 才显示
                  - 桌面端不受影响（PC 用户保留入口）*/}
              <div className="hidden md:flex shrink-0">
                <ImageUploadButton
                  images={form.uploadedImages}
                  onChange={(updater) => {
                    if (typeof updater === 'function') {
                      setForm((prev) => {
                        const fn = updater as (prev: UploadedImage[]) => UploadedImage[];
                        return { ...prev, uploadedImages: fn(prev.uploadedImages) };
                      });
                    } else {
                      updateForm('uploadedImages', updater);
                    }
                  }}
                  disabled={false}
                />
              </div>

              <div className="flex-1 min-w-0">
                <GenerationToolbar
                  onSettingsOpen={(section) => {
                    setSettingsSection(section);
                    setSettingsOpen(true);
                  }}
                  pdfFile={form.pdfFile}
                  webSearch={webSearch}
                  onWebSearchChange={setWebSearch}
                  onPdfFileChange={(f) => updateForm('pdfFile', f)}
                  onPdfError={setError}
                  // 【2026-07-29 多文件上传 additive】镜像官方 OpenMAIC 多文件上传 props
                  courseMaterials={form.courseMaterials}
                  onCourseMaterialsAdd={addCourseMaterials}
                  onCourseMaterialRemove={removeCourseMaterial}
                />
              </div>

              {/* AI课程规划 button — v6.97.19 hover tooltip 解释按钮功能
                  【2026-07-31 v2 老板需求】手机版去掉 AI规划课程 按钮（mobile 用户主流程是快速进课堂/演练，不需要规划入口）
                  - hidden md:flex → 默认 hidden, ≥768px 才显示
                  - 保留 data-plan-btn 属性：弹窗收缩动画仍可定位
                  - 桌面端不受影响（PC 用户保留入口）*/}
              <AiPlanTooltip>
                <button
                  onClick={() => { window.location.href = '/dongniai/plan/myForms'; }}
                  data-plan-btn
                  className="hidden md:flex shrink-0 h-8 rounded-lg items-center justify-center gap-1.5 transition-all px-3 bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:opacity-90 shadow-sm cursor-pointer"
                >
                  <Sparkles className="size-3.5" />
                  <span className="text-xs font-medium whitespace-nowrap">AI规划课程</span>
                </button>
              </AiPlanTooltip>

              {/* Interactive mode toggle — hidden when admin disables it */}
              {adminInteractiveEnabled !== false && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                      onClick={() => updateForm('interactiveMode', !form.interactiveMode)}
                      className={cn(
                        'relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all cursor-pointer select-none whitespace-nowrap border shrink-0 h-8',
                        form.interactiveMode
                          ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border-cyan-500 shadow-[0_0_12px_rgba(6,182,212,0.35)] dark:shadow-[0_0_12px_rgba(6,182,212,0.25)]'
                          : 'border-cyan-300/60 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/20',
                      )}
                    >
                      {form.interactiveMode && (
                        <span
                          className="absolute inset-[-4px] rounded-full border border-cyan-400/40 dark:border-cyan-400/25"
                          style={{
                            animation: 'interactive-mode-breathe 2s ease-in-out infinite',
                          }}
                        />
                      )}
                      <Atom className="size-3.5 relative z-10 animate-[spin_3s_linear_infinite]" />
                      <span className="relative z-10">{t('toolbar.interactiveModeLabel')}</span>
                    </motion.button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    {t('toolbar.interactiveModeHint')}
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Voice input */}
              <SpeechButton
                size="md"
                onTranscription={(text) => {
                  setForm((prev) => {
                    const next = prev.requirement + (prev.requirement ? ' ' : '') + text;
                    updateRequirementCache(next);
                    return { ...prev, requirement: next };
                  });
                }}
              />

              {/* 2026-07-30 v2 老板需求：去"习题演练"按钮 + 去"进入习题"按钮，新增"学员详情"常亮按钮（弹学员详情配置弹窗）
                   - 关键：常亮 = 不依赖表单是否填写，随时可以点击进来查看/编辑学员情况
                   - 不依赖 canGenerate（不像"立即上课"要 textarea 有内容才激活）
                   - 学员详情配置会拼成"【学生基础水平】+【阶段】+【年级】+【科目】+【教材版本】"插入到 textarea
                   - 之前状态（已填写 / 未填写）通过 title 提示给用户 */}
              <button
                onClick={() => {
                  openExerciseConfig();
                }}
                title={exerciseConfig.education_level && exerciseConfig.subject
                  ? `已配置：${exerciseConfig.education_level} · ${exerciseConfig.education_grade || ''} · ${exerciseConfig.subject} · ${exerciseConfig.textbook_version || ''} · 基础水平：${exerciseConfig.student_level === '其他' ? (exerciseConfig.student_level_other || '其他') : exerciseConfig.student_level}`
                  : '学员详情（点击填写/查看学员基本情况）'}
                className={cn(
                  'shrink-0 h-8 rounded-lg flex items-center justify-center gap-1.5 transition-all px-3 border cursor-pointer',
                  // 2026-07-30 v2 老板需求：常亮 — 用稳定的青绿边框（与顶部"插入学情"区分开，避免视觉冲突）
                  'border-teal-500/70 text-teal-700 bg-teal-50/60 hover:bg-teal-100 hover:border-teal-600',
                )}
              >
                <UserCircle2 className="size-3.5" />
                <span className="text-xs font-medium">学员详情</span>
              </button>
              {/* Send button */}
              <button
                onClick={handleGenerate}
                disabled={!canGenerate}
                className={cn(
                  'shrink-0 h-8 rounded-lg flex items-center justify-center gap-1.5 transition-all px-3',
                  canGenerate
                    ? 'bg-primary text-primary-foreground hover:opacity-90 shadow-sm cursor-pointer'
                    : 'bg-muted text-muted-foreground/40 cursor-not-allowed',
                )}
              >
                <span className="text-xs font-medium">{t('toolbar.enterClassroom')}</span>
                <ArrowUp className="size-3.5" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* ── OCR Overlay (点击进入课堂且带图片时) ── */}
        <AnimatePresence>
          {ocrOverlay && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
              data-ocr-overlay
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl px-8 py-6 flex flex-col items-center gap-3 max-w-sm"
              >
                <div className="size-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Loader2 className="size-6 animate-spin text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-sm font-medium">{ocrOverlay.message}</p>
                <p className="text-xs text-muted-foreground">请稍候，这通常需要 5-15 秒</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Error ── */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 w-full p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
            >
              <p className="text-sm text-destructive">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Import button (empty state) ── */}
        {classrooms.length === 0 && (
          <button
            onClick={triggerFileSelect}
            disabled={importing}
            className="relative z-10 mt-4 flex items-center gap-1.5 text-[12px] text-muted-foreground/40 hover:text-foreground/60 transition-colors"
          >
            <Upload className="size-3.5" />
            <span>{t('import.classroom')}</span>
          </button>
        )}
      </motion.div>



      {/* Course Catalog Card — absolute positioned right of input, inside hero column */}
      <CourseCatalogCardWrapper />

      {/* ═══ Recent classrooms — collapsible ═══ */}
      {classrooms.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="relative z-10 mt-10 w-full max-w-6xl flex flex-col items-center"
        >
          {/* Trigger — divider-line with centered text */}
          <div className="group w-full flex items-center gap-4 py-2">
            <div className="flex-1 h-px bg-border/40 group-hover:bg-border/70 transition-colors" />
            <div className="shrink-0 flex items-center gap-3 text-[13px] text-muted-foreground/60 select-none">
              <button
                onClick={() => persistRecentOpen(!recentOpen)}
                className="flex items-center gap-2 hover:text-foreground/70 transition-colors cursor-pointer"
              >
                <Clock className="size-3.5" />
                {t('classroom.recentClassrooms')}
                <span className="text-[11px] tabular-nums opacity-60">{classrooms.length}</span>
                <motion.div
                  animate={{ rotate: recentOpen ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <ChevronDown className="size-3.5" />
                </motion.div>
              </button>

              {/* Search toggle — icon that expands into an input in place */}
              <AnimatePresence initial={false}>
                {!searchOpen ? (
                  <motion.button
                    key="search-icon"
                    ref={searchButtonRef}
                    type="button"
                    aria-label={t('classroom.searchAriaLabel')}
                    onClick={() => {
                      setSearchOpen(true);
                      if (!recentOpen) persistRecentOpen(true);
                      requestAnimationFrame(() => searchInputRef.current?.focus());
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12, ease: 'easeOut' }}
                    className="flex items-center justify-center size-6 rounded-full text-muted-foreground/50 hover:text-foreground/70 hover:bg-muted/50 transition-colors cursor-pointer"
                  >
                    <Search className="size-3.5" />
                  </motion.button>
                ) : (
                  <motion.div
                    key="search-input"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 200 }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
                    className="overflow-hidden"
                  >
                    <InputGroup
                      className={cn(
                        'h-7 text-[12px] rounded-full bg-muted/40 border-transparent shadow-none',
                        'transition-colors',
                        'hover:bg-muted/60',
                        'has-[[data-slot=input-group-control]:focus-visible]:bg-muted/60',
                        'has-[[data-slot=input-group-control]:focus-visible]:border-transparent',
                        'has-[[data-slot=input-group-control]:focus-visible]:ring-0',
                      )}
                    >
                      <InputGroupInput
                        ref={searchInputRef}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            if (searchQuery) {
                              setSearchQuery('');
                            } else {
                              setSearchOpen(false);
                              requestAnimationFrame(() => searchButtonRef.current?.focus());
                            }
                          }
                        }}
                        onBlur={() => {
                          if (!searchQuery) {
                            setSearchOpen(false);
                          }
                        }}
                        placeholder={t('classroom.searchPlaceholder')}
                        aria-label={t('classroom.searchAriaLabel')}
                        className="h-7 pl-3 placeholder:text-muted-foreground/50"
                      />
                      {searchQuery && (
                        <InputGroupButton
                          size="icon-xs"
                          aria-label={t('classroom.clearSearch')}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSearchQuery('');
                            searchInputRef.current?.focus();
                          }}
                        >
                          <X />
                        </InputGroupButton>
                      )}
                    </InputGroup>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                onClick={triggerFileSelect}
                disabled={importing}
                className="group/import grid grid-cols-[auto_0fr] hover:grid-cols-[auto_1fr] items-center gap-1 rounded-full px-1.5 py-0.5 text-[12px] text-muted-foreground/35 hover:text-muted-foreground/70 hover:bg-muted/50 transition-all duration-200 cursor-pointer"
              >
                <Upload className="size-3" />
                <span className="overflow-hidden opacity-0 group-hover/import:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                  {t('import.classroom')}
                </span>
              </button>
            </div>
            <div className="flex-1 h-px bg-border/40 group-hover:bg-border/70 transition-colors" />
          </div>

          {/* Expandable content */}
          <AnimatePresence>
            {recentOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                className="w-full overflow-hidden"
              >
                {searchQuery.trim() && filteredClassrooms.length === 0 ? (
                  <div className="pt-8 pb-2 text-center text-[13px] text-muted-foreground/60">
                    {t('classroom.searchEmpty')}
                  </div>
                ) : (
                  <div className="pt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-8">
                    {filteredClassrooms.map((classroom, i) => (
                      <motion.div
                        key={classroom.id}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: i * 0.04,
                          duration: 0.35,
                          ease: 'easeOut',
                        }}
                      >
                        <ClassroomCard
                          classroom={classroom}
                          slide={thumbnails[classroom.id]}
                          formatDate={formatDate}
                          onDelete={handleDelete}
                          onRename={handleRename}
                          confirmingDelete={pendingDeleteId === classroom.id}
                          onConfirmDelete={() => confirmDelete(classroom.id)}
                          onCancelDelete={() => setPendingDeleteId(null)}
                          onClick={() => {
                            const { loggedIn } = useAuthStore.getState();
                            if (!loggedIn) {
                              // 【v6.97.83+5 P0 修复 2026-09-01 老板报】按 v6.97.67 模板升级绝对路径
                              window.location.href =
                                window.location.origin +
                                `/index/user/login.html?url=${encodeURIComponent(window.location.href)}`;
                              return;
                            }
                            router.push(`/classroom/${classroom.id}`);
                          }}
                        />
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Footer — flows with content, at the very end */}
      <div className="mt-auto pt-12 pb-4 text-center text-xs text-muted-foreground/40">
        Dongniai
      </div>
    </div>
    </>
  );
}

// ─── Greeting Bar — avatar + "Hi, Name", click to edit in-place ────
const MAX_AVATAR_SIZE = 5 * 1024 * 1024;

function isCustomAvatar(src: string) {
  return src.startsWith('data:');
}

function GreetingBar() {
  const { t } = useI18n();
  const avatar = useUserProfileStore((s) => s.avatar);
  const nickname = useUserProfileStore((s) => s.nickname);
  const bio = useUserProfileStore((s) => s.bio);
  const setAvatar = useUserProfileStore((s) => s.setAvatar);
  const setNickname = useUserProfileStore((s) => s.setNickname);
  const setBio = useUserProfileStore((s) => s.setBio);

  const [open, setOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Always address as '同学' regardless of user's nickname
  const displayName = '同学';

  // Click-outside to collapse
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setEditingName(false);
        setAvatarPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const startEditName = () => {
    setNameDraft(nickname);
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  const commitName = () => {
    setNickname(nameDraft.trim());
    setEditingName(false);
  };

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_AVATAR_SIZE) {
      toast.error(t('profile.fileTooLarge'));
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error(t('profile.invalidFileType'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 128;
        const ctx = canvas.getContext('2d')!;
        const scale = Math.max(128 / img.width, 128 / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (128 - w) / 2, (128 - h) / 2, w, h);
        setAvatar(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div ref={containerRef} className="relative pl-4 pr-2 pt-3.5 pb-1 w-auto">
      <input
        ref={avatarInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleAvatarUpload}
      />

      {/* ── Collapsed pill (always in flow) ── */}
      {!open && (
        <div
          className="flex items-center gap-2.5 cursor-pointer transition-all duration-200 group rounded-full px-2.5 py-1.5 border border-border/50 text-muted-foreground/70 hover:text-foreground hover:bg-muted/60 active:scale-[0.97]"
          onClick={() => setOpen(true)}
        >
          <div className="shrink-0 relative">
            <div className="size-8 rounded-full overflow-hidden ring-[1.5px] ring-border/30 group-hover:ring-violet-400/60 dark:group-hover:ring-violet-400/40 transition-all duration-300">
              <img src={avatar} alt="" className="size-full object-cover" />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full bg-white dark:bg-slate-800 border border-border/40 flex items-center justify-center opacity-60 group-hover:opacity-100 transition-opacity">
              <Pencil className="size-[7px] text-muted-foreground/70" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="leading-none select-none flex items-center gap-1">
                  <span className="text-[13px] font-semibold text-foreground/85 group-hover:text-foreground transition-colors">
                    {t('home.greetingWithName', { name: displayName })}
                  </span>
                  <ChevronDown className="size-3 text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors shrink-0" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={4}>
                {t('profile.editTooltip')}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      )}

      {/* ── Expanded panel (absolute, floating) ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="absolute left-4 top-3.5 z-50 w-64"
          >
            <div className="rounded-2xl bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06] shadow-[0_1px_8px_-2px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_8px_-2px_rgba(0,0,0,0.3)] px-2.5 py-2">
              {/* ── Row: avatar + name ── */}
              <div
                className="flex items-center gap-2.5 cursor-pointer transition-all duration-200"
                onClick={() => {
                  setOpen(false);
                  setEditingName(false);
                  setAvatarPickerOpen(false);
                }}
              >
                {/* Avatar */}
                <div
                  className="shrink-0 relative cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAvatarPickerOpen(!avatarPickerOpen);
                  }}
                >
                  <div className="size-8 rounded-full overflow-hidden ring-[1.5px] ring-violet-300/70 dark:ring-violet-500/40 transition-all duration-300">
                    <img src={avatar} alt="" className="size-full object-cover" />
                  </div>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full bg-white dark:bg-slate-800 border border-border/60 flex items-center justify-center"
                  >
                    <ChevronDown
                      className={cn(
                        'size-2 text-muted-foreground/70 transition-transform duration-200',
                        avatarPickerOpen && 'rotate-180',
                      )}
                    />
                  </motion.div>
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  {editingName ? (
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        ref={nameInputRef}
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') commitName();
                          if (e.key === 'Escape') {
                            setEditingName(false);
                          }
                        }}
                        onBlur={commitName}
                        maxLength={20}
                        placeholder={t('profile.defaultNickname')}
                        className="flex-1 min-w-0 h-6 bg-transparent border-b border-border/80 text-[13px] font-semibold text-foreground outline-none placeholder:text-muted-foreground/40"
                      />
                      <button
                        onClick={commitName}
                        className="shrink-0 size-5 rounded flex items-center justify-center text-violet-500 hover:bg-violet-100 dark:hover:bg-violet-900/30"
                      >
                        <Check className="size-3" />
                      </button>
                    </div>
                  ) : (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditName();
                      }}
                      className="group/name inline-flex items-center gap-1 cursor-pointer"
                    >
                      <span className="text-[13px] font-semibold text-foreground/85 group-hover/name:text-foreground transition-colors">
                        {displayName}
                      </span>
                      <Pencil className="size-2.5 text-muted-foreground/30 opacity-0 group-hover/name:opacity-100 transition-opacity" />
                    </span>
                  )}
                </div>

                {/* Collapse arrow */}
                <motion.div
                  initial={{ opacity: 0, y: -2 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="shrink-0 size-6 rounded-full flex items-center justify-center hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
                >
                  <ChevronUp className="size-3.5 text-muted-foreground/50" />
                </motion.div>
              </div>

              {/* ── Expandable content ── */}
              <div className="pt-2" onClick={(e) => e.stopPropagation()}>
                {/* Avatar picker */}
                <AnimatePresence>
                  {avatarPickerOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="p-1 pb-2.5 flex items-center gap-1.5 flex-wrap">
                        {AVATAR_OPTIONS.map((url) => (
                          <button
                            key={url}
                            onClick={() => setAvatar(url)}
                            className={cn(
                              'size-7 rounded-full overflow-hidden bg-gray-50 dark:bg-gray-800 cursor-pointer transition-all duration-150',
                              'hover:scale-110 active:scale-95',
                              avatar === url
                                ? 'ring-2 ring-violet-400 dark:ring-violet-500 ring-offset-0'
                                : 'hover:ring-1 hover:ring-muted-foreground/30',
                            )}
                          >
                            <img src={url} alt="" className="size-full" />
                          </button>
                        ))}
                        <label
                          className={cn(
                            'size-7 rounded-full flex items-center justify-center cursor-pointer transition-all duration-150 border border-dashed',
                            'hover:scale-110 active:scale-95',
                            isCustomAvatar(avatar)
                              ? 'ring-2 ring-violet-400 dark:ring-violet-500 ring-offset-0 border-violet-300 dark:border-violet-600 bg-violet-50 dark:bg-violet-900/30'
                              : 'border-muted-foreground/30 text-muted-foreground/50 hover:border-muted-foreground/50',
                          )}
                          onClick={() => avatarInputRef.current?.click()}
                          title={t('profile.uploadAvatar')}
                        >
                          <ImagePlus className="size-3" />
                        </label>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Bio */}
                <UITextarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder={t('profile.bioPlaceholder')}
                  maxLength={200}
                  rows={2}
                  className="resize-none border-border/40 bg-transparent min-h-[72px] !text-[13px] !leading-relaxed placeholder:!text-[11px] placeholder:!leading-relaxed focus-visible:ring-1 focus-visible:ring-border/60"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* 【v6.98.0 限时优惠弹窗】未登录访客 — 注册赠 45 分钟（30 天有效）。内部自带登录态检查 + 7 天记忆 */}
      <TrialOfferPopup />
    </div>
  );
}

// ─── Classroom Card — clean, minimal style ──────────────────────
function ClassroomCard({
  classroom,
  slide,
  formatDate,
  onDelete,
  onRename,
  confirmingDelete,
  onConfirmDelete,
  onCancelDelete,
  onClick,
}: {
  classroom: StageListItem;
  slide?: Slide;
  formatDate: (ts: number) => string;
  onDelete: (id: string, e: React.MouseEvent) => void;
  onRename: (id: string, newName: string) => void;
  confirmingDelete: boolean;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  onClick: () => void;
}) {
  const { t } = useI18n();
  const thumbRef = useRef<HTMLDivElement>(null);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = thumbRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setThumbWidth(Math.round(entry.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (editing) nameInputRef.current?.focus();
  }, [editing]);

  // Load admin defaults on mount so model/provider is pre-configured
  useEffect(() => {
    useSettingsStore.getState().fetchAdminDefaults();
  }, []);

  const startRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNameDraft(classroom.name);
    setEditing(true);
  };

  const commitRename = () => {
    if (!editing) return;
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== classroom.name) {
      onRename(classroom.id, trimmed);
    }
    setEditing(false);
  };

  return (
    <div className="group cursor-pointer" onClick={confirmingDelete ? undefined : onClick}>
      {/* Thumbnail — large radius, no border, subtle bg */}
      <div
        ref={thumbRef}
        className="relative w-full aspect-[16/9] rounded-2xl bg-slate-100 dark:bg-slate-800/80 overflow-hidden transition-transform duration-200 group-hover:scale-[1.02]"
      >
        {slide && thumbWidth > 0 ? (
          <ThumbnailSlide
            slide={slide}
            size={thumbWidth}
            viewportSize={slide.viewportSize ?? 1000}
            viewportRatio={slide.viewportRatio ?? 0.5625}
          />
        ) : !slide ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="size-12 rounded-2xl bg-gradient-to-br from-violet-100 to-blue-100 dark:from-violet-900/30 dark:to-blue-900/30 flex items-center justify-center">
              <span className="text-xl opacity-50">📄</span>
            </div>
          </div>
        ) : null}

        {classroom.interactiveMode && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                aria-label={t('toolbar.interactiveModeLabel')}
                onClick={(e) => e.stopPropagation()}
                className="absolute bottom-2 left-2 inline-flex items-center justify-center size-5 rounded-full bg-white/70 dark:bg-slate-900/60 text-cyan-600 dark:text-cyan-300 backdrop-blur-sm shadow-sm ring-1 ring-cyan-500/30 z-10"
              >
                <Atom className="size-3" />
              </span>
            </TooltipTrigger>
            {/* Negative sideOffset compensates for the global Tooltip Arrow's
                rotate-45 bounding box, which Radix reserves as spacing. */}
            <TooltipContent
              side="top"
              align="start"
              sideOffset={-4}
              collisionPadding={0}
              className="text-xs"
            >
              {t('toolbar.interactiveModeLabel')}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Delete — top-right, only on hover */}
        <AnimatePresence>
          {!confirmingDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Button
                size="icon"
                variant="ghost"
                className="absolute top-2 right-2 size-7 opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 hover:bg-destructive/80 text-white hover:text-white backdrop-blur-sm rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(classroom.id, e);
                }}
              >
                <Trash2 className="size-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="absolute top-2 right-11 size-7 opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 hover:bg-black/50 text-white hover:text-white backdrop-blur-sm rounded-full"
                onClick={startRename}
              >
                <Pencil className="size-3.5" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Inline delete confirmation overlay */}
        <AnimatePresence>
          {confirmingDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black/50 backdrop-blur-[6px]"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-[13px] font-medium text-white/90">
                {t('classroom.deleteConfirmTitle')}?
              </span>
              <div className="flex gap-2">
                <button
                  className="px-3.5 py-1 rounded-lg text-[12px] font-medium bg-white/15 text-white/80 hover:bg-white/25 backdrop-blur-sm transition-colors"
                  onClick={onCancelDelete}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="px-3.5 py-1 rounded-lg text-[12px] font-medium bg-red-500/90 text-white hover:bg-red-500 transition-colors"
                  onClick={onConfirmDelete}
                >
                  {t('classroom.delete')}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Info — outside the thumbnail */}
      <div className="mt-2.5 px-1 flex items-center gap-2">
        <span className="shrink-0 inline-flex items-center rounded-full bg-violet-100 dark:bg-violet-900/30 px-2 py-0.5 text-[11px] font-medium text-violet-600 dark:text-violet-400">
          {classroom.sceneCount} {t('classroom.slides')} · <span suppressHydrationWarning>{formatDate(classroom.updatedAt)}</span>
        </span>
        {editing ? (
          <div className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
            <input
              ref={nameInputRef}
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setEditing(false);
              }}
              onBlur={commitRename}
              maxLength={100}
              placeholder={t('classroom.renamePlaceholder')}
              className="w-full bg-transparent border-b border-violet-400/60 text-[15px] font-medium text-foreground/90 outline-none placeholder:text-muted-foreground/40"
            />
          </div>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <p
                className="font-medium text-[15px] truncate text-foreground/90 min-w-0 cursor-text"
                onDoubleClick={startRename}
              >
                {classroom.name}
              </p>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              sideOffset={4}
              className="!max-w-[min(90vw,32rem)] break-words whitespace-normal"
            >
              <div className="flex items-center gap-1.5">
                <span className="break-all">{classroom.name}</span>
                <button
                  className="shrink-0 p-0.5 rounded hover:bg-foreground/10 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(classroom.name);
                    toast.success(t('classroom.nameCopied'));
                  }}
                >
                  <Copy className="size-3 opacity-60" />
                </button>
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default function Page() {
  return <HomePage />;
}
